// МАСТЕР «ЦЕПОЧКА РАССЫЛОК» (бэклог «Цепочки 2026-08-31», заказ владельца 01.09.2026).
//
// Цепочка — серия писем, которая уходит человеку ПО ДНЯМ после того, как он закончил
// другого бота или рассылку. Живёт она в «Ботах» (в «Рассылках» осталось только разовое
// сообщение), а собирается вот этой анкетой — той же панелью и теми же элементами, что
// три мастера-шаблона: своей формы и своих стилей у цепочки нет.
//
// ЭТО ВТОРОЙ ПРОДУКТ — все входы закрыты флагом CHAINS_HIDDEN (lib/hidden-features.ts):
// код, адреса и мастер живы, не рисуются только двери.
//
// ЧЕМ ОТЛИЧАЕТСЯ ОТ ОСТАЛЬНЫХ АНКЕТ:
//  • шаблона нет (standalone) — «схема» рождается целиком из ответов;
//  • страниц-писем сколько угодно (repeatPages): по странице на письмо;
//  • ЗАПУСК ТОЛЬКО СОБЫТИЕМ — своих условий запуска (слово, комментарий, дата) у цепочки
//    нет вовсе: вход один, «человек закончил вот этих ботов».
//
// ГЛАВНОЕ ПРО УСТРОЙСТВО: письма НЕ ждут своей очереди внутри сценария. Ожиданием
// владеет наша база (bot_chains + chainPlan в @scriptera/db), а собранная схема — это
// честная картинка цепочки для карточки бота, не исполняемый сценарий. Причина —
// разбор владельца 31.08: спящий прогон делает переключатель «применить к тем, кто уже
// внутри» физически невозможным.
import {
  chatStartJson,
  TELEGRAM_MESSAGE_RULES,
  type CatalogParam,
  type WorkflowJson,
  type WorkflowNode,
} from '@scriptera/ui/scenario';
// INVARIANT(scriptera): ядро цепочки берётся ПОДПУТЁМ `@scriptera/db/bot-chains`, а не
// из корня `@scriptera/db`. Причина: корень пакета тянет за собой сгенерированный
// Prisma-клиент (Node-only, `fs`), а анкеты читает и КЛИЕНТСКАЯ сторона кабинета —
// сборка web валится «Module not found: fs» и dev-сервер ложится целиком. Здесь только
// данные и чистые функции; всё, что ходит в базу, живёт в server-only модулях
// (lib/chain-engine.ts) и в воркере. Тот же приём, что у notify-catalog и free-signature.
import {
  CHAIN_MAX_ENTRIES,
  CHAIN_WAIT_UNITS,
  chainChatEntries,
  chainDelayMin,
  chainEntriesOfAudience,
  chainWatch,
  isServiceMark,
  parseBotMark,
  toChainAudience,
  type AudienceFilters,
  type ChainButton,
  type ChainEntry,
  type ChainLetter,
  renameTagInDraft,
  renameTagInEntries,
  renameTagInFilters,
} from '@scriptera/db/bot-chains';
// Виды входа Телеграма — ОДИН свод на продукт (bot-entries): порядок пяти чат-входов
// цепочки тот же, что у обычного бота.
import { botEntry, TG_ENTRY_KINDS, type BotEntry, type BotEntryKind } from '@scriptera/db/bot-entries';
import { draftIsOneMessage, draftShape, htmlToPlain, isEmptyHtml, toTgDraft, type TgDraft } from '@scriptera/db/tg-message';
import {
  ansNum,
  ansStr,
  wizAccountParam,
  wizAttachNode,
  wizEntryWords,
  wizNodeTitle,
  type TemplateWizardDef,
  type WizardItemAddKind,
  type WizardItemLive,
  type WizardLocale,
  type WizardStep,
  aboutStep,
} from './core';
// ОДИН СТРОИТЕЛЬ ШАГА «ЧТО ЗАПУСКАЕТ» на цепочку и на ботов (К18.1 п.9–10): меню видов,
// страницы полей, строки, черновик и «Удалить» живут ТАМ. У цепочки сверху пяти общих
// чат-видов стоят два своих — «Прошёл бота» и «Получил метку».
import {
  trigAdd,
  trigAddKinds,
  trigApply,
  trigBlocked,
  trigFields,
  trigLine,
  trigTitle,
  trigValues,
  triggerStep,
  type TriggerOwn,
} from './trigger-step';
// Слова условий складывает ОДНА функция на весь продукт (И3): строка формы, подстрока
// письма в списке и прогноз на карточке человека читают её же. Модуль чистый (типовые
// импорты стираются), поэтому анкете он не тяжелее собственного словаря.
import { audienceWords, type AudienceWordsDict } from '@/components/cabinet/audience-value';
import type { SceneTexts } from '@/lib/wizard-scene-texts';

/** Слаг анкеты — им «Боты» открывают мастер цепочки (wizardById). */
export const CHAIN_WIZARD_ID = 'chain';

/** Тип узла «Начало цепочки» в собранной картинке схемы. */
export const CHAIN_START_TYPE = 'CUSTOM.scripteraChainStart';

/**
 * Сколько писем помещается в одну цепочку. Кап нужен: ответы анкеты живут в jsonb
 * черновика с пределом MAX_WIZARD_ANSWERS. Двадцать — заведомо больше любого догрева.
 */
export const CHAIN_MAX_LETTERS = 20;

/** Имена ответов ОДНОГО письма (ответы анкеты плоские — номер письма в имени поля).
 *  Имена `msg…` взяты нарочно: панель мастера уже знает их и даёт письму заливку
 *  картинки и список кнопок — второго контрола под то же самое не заводим.
 *
 *  INVARIANT(scriptera): новое поле сообщения обязано попасть в LETTER_FIELDS, иначе
 *  удаление/перестановка перемешают его между сообщениями. Причина: П132 п.6 (24–25.09.2026)
 *  — название `msgName<i>` в список не попало, и после удаления первого сообщения его
 *  название доставалось соседу. Тест «каждое поле chainField сдвигается вместе с
 *  сообщением» в chain.test.ts проверяет ВСЕ ключи этого объекта.
 *  Канон: docs/NATIVE-DESIGN-FIXLIST.md ТЗ П132. */
export const chainField = {
  /** НАЗВАНИЕ ПИСЬМА ДЛЯ СЕБЯ (П129.10, владелец 24.09.2026): заголовок карточки и строка
   *  списка; клиенту не уходит. Пусто — «Сообщение N». */
  name: (i: number) => `msgName${i}`,
  text: (i: number) => `msgText${i}`,
  photo: (i: number) => `msgPhoto${i}`,
  buttons: (i: number) => `msgBtns${i}`,
  waitAmount: (i: number) => `waitAmount${i}`,
  waitUnit: (i: number) => `waitUnit${i}`,
  /** СОБРАННОЕ ОКНОМ «СООБЩЕНИЕ» письмо (модель TgDraft) строкой JSON — как `audience`
   *  у рассылки держит отбор. Пусто — письмо старого вида (текст/картинка/кнопки). */
  message: (i: number) => `msg${i}`,
  /**
   * ПРЕЖНИЙ ЗАПУСК ЦЕПОЧКИ — условия ПЕРВОГО письма строкой JSON. С 05.09.2026 (К4)
   * запуск живёт СПИСКОМ ВХОДОВ в ответе `entries`, и этот ключ БОЛЬШЕ НЕ ПИШЕТСЯ.
   * Читается ровно в одном месте — адаптером старого черновика (chainEntries).
   */
  audience: (i: number) => `aud${i}`,
  /**
   * «КОМУ» ЭТОГО СООБЩЕНИЯ (владелец 04.09.2026, И10 п.10) — условия строкой JSON.
   * Отдельно от `aud<i>` НАРОЧНО: `aud1` — это ЗАПУСК цепочки (он живёт на шаге «С чего
   * начинается» и на странице сообщения не показывается), а «Кому» есть у КАЖДОГО
   * сообщения, включая первое, и проверяется в момент отправки. Пусто — «все, кто дошёл
   * до этого сообщения».
   */
  cond: (i: number) => `cond${i}`,
} as const;

/** Ответы шага «Письма», которые есть у КАЖДОГО письма (их сдвигает удаление и
 *  перестановка). Пары «имя ответа → умолчание» — один список на добавление, удаление и
 *  перестановку: разъедутся — письмо потеряет часть настроек. */
const LETTER_FIELDS: Array<[(i: number) => string, unknown]> = [
  // НАЗВАНИЕ ДЛЯ СЕБЯ (П132 п.6): без него удаление и перестановка оставляли названия на
  // местах, и «С1» доставалось бывшему «С2».
  [chainField.name, ''],
  [chainField.text, ''],
  [chainField.photo, ''],
  [chainField.buttons, ''],
  [chainField.message, ''],
  [chainField.waitAmount, 1],
  [chainField.waitUnit, 'days'],
  [chainField.audience, ''],
  [chainField.cond, ''],
];

/** Ключ страницы письма номер `i` (по нему панель узнаёт страницу-повтор). */
export const chainLetterStepKey = (i: number): string => `letter${i}`;

/** Номер письма по ключу страницы; 0 — страница не про письмо. */
export function chainLetterOfStep(key: string): number {
  const m = /^letter(\d+)$/.exec(key);
  return m ? Number(m[1]) : 0;
}

/** Сколько писем сейчас в анкете (1 … CHAIN_MAX_LETTERS). */
export function chainLetterCount(answers: Record<string, unknown>): number {
  return ansNum(answers, 'letters', { min: 1, max: CHAIN_MAX_LETTERS, fallback: 1 });
}

/**
 * СТАБИЛЬНЫЕ ID ПИСЕМ (требование владельца 01.09.2026, пункт 1). Позиция письма в
 * анкете меняется — ключ нет: по нему журнал узнаёт «это письмо человек уже получал».
 * Живут ключи одной строкой ответов, по ключу на позицию: 'l1,l2,l5'.
 * Ответы старого черновика без этой строки читаются как l1…lN — до первой перестановки
 * это ровно те же ключи, а перестановки у такого черновика ещё не было.
 */
export function chainLetterKeys(answers: Record<string, unknown>): string[] {
  const n = chainLetterCount(answers);
  const raw = ansStr(answers, 'letterKeys', 2000)
    .split(',')
    .map((s) => s.trim())
    .filter((s) => /^l\d+$/.test(s));
  const out: string[] = [];
  for (let i = 0; i < n; i++) out.push(raw[i] ?? `l${i + 1}`);
  return out;
}

/** Последний выданный номер ключа: новые письма НИКОГДА не переиспользуют старый ключ. */
function chainKeySeq(answers: Record<string, unknown>): number {
  const used = chainLetterKeys(answers).map((k) => Number(k.slice(1)));
  const stored = ansNum(answers, 'letterSeq', { min: 0, max: 100_000, fallback: 0 });
  return Math.max(stored, ...used, 0);
}

/** Кнопка письма: подпись плюс адрес (пусто — кнопка без ссылки, её нажатие мы считаем). */
export function chainButtons(raw: string): ChainButton[] {
  return raw
    .split('\n')
    .map((line) => {
      const at = line.indexOf('|');
      const text = (at === -1 ? line : line.slice(0, at)).trim().slice(0, 64);
      const url = at === -1 ? '' : line.slice(at + 1).trim().slice(0, 500);
      return { text, url: /^https?:\/\//i.test(url) ? url : '' };
    })
    .filter((b) => b.text !== '')
    .slice(0, 8);
}

/**
 * ПИСЬМА ЦЕПОЧКИ из ответов анкеты — ровно в том виде, в каком их кладёт в базу движок
 * (ключ, место, пауза в минутах, текст, картинка, кнопки). У первого письма паузы не
 * бывает: перед ним ничего не было, оно уходит сразу после входа.
 */
export function chainLetters(answers: Record<string, unknown>): ChainLetter[] {
  const keys = chainLetterKeys(answers);
  return keys.map((key, idx) => {
    const i = idx + 1;
    const unit = ansStr(answers, chainField.waitUnit(i), 8, 'days');
    // У ПЕРВОГО СООБЩЕНИЯ ТЕПЕРЬ ТОЖЕ БЫВАЕТ ПАУЗА (владелец 04.09.2026, И10 п.10):
    // «Сразу после запуска» или «через N часов/дней после запуска». Раньше она
    // принудительно обнулялась — и «письмо через сутки после входа» выразить было нечем.
    const amount = ansNum(answers, chainField.waitAmount(i), { min: 0, max: 365, fallback: 0 });
    const cond = chainConditions(answers, i);
    const draft = chainMessage(answers, i);
    // ЗАПУСК В ПИСЬМО НЕ КЛАДЁТСЯ (К4): он живёт СПИСКОМ ВХОДОВ в версии цепочки, и
    // прежний `aud1` новыми сохранениями не пишется вовсе.
    return {
      key,
      ord: idx,
      delayMin: chainDelayMin(amount, (CHAIN_WAIT_UNITS as readonly string[]).includes(unit) ? unit : 'days'),
      text: ansStr(answers, chainField.text(i), 3000),
      photo: ansStr(answers, chainField.photo(i), 500),
      buttons: chainButtons(ansStr(answers, chainField.buttons(i), 2000)),
      ...(cond ? { conditions: cond } : {}),
      ...(draft ? { message: draft } : {}),
    };
  });
}

/** СОБРАННОЕ ОКНОМ ПИСЬМО номера `i`; null — письмо ещё старого вида. */
export function chainMessage(answers: Record<string, unknown>, i: number): TgDraft | null {
  const raw = ansStr(answers, chainField.message(i), 200_000);
  if (raw === '') return null;
  try {
    return toTgDraft(JSON.parse(raw));
  } catch {
    // Битый JSON — не повод потерять письмо: уйдут прежние поля.
    return null;
  }
}

/**
 * УСЛОВИЯ «КОМУ» письма номера `i`; null — своих условий нет («как у предыдущего»).
 * Ответ живёт строкой JSON, разбирает его тот же код, что условия рассылки.
 */
export function chainAudience(answers: Record<string, unknown>, i: number): AudienceFilters | null {
  return toChainAudience(ansStr(answers, chainField.audience(i), 8000));
}

/**
 * «КОМУ» СООБЩЕНИЯ номера `i` (И10 п.10); null — своих условий нет, значит «все, кто
 * дошёл до этого сообщения». Разбирает их тот же код, что условия рассылки.
 */
export function chainConditions(answers: Record<string, unknown>, i: number): AudienceFilters | null {
  return toChainAudience(ansStr(answers, chainField.cond(i), 8000));
}

// ── ВХОДЫ ЦЕПОЧКИ — СПИСОК ВХОДОВ, КАК У БОТОВ (раздел К, владелец 05.09.2026) ────────
//
// Прежде запуск притворялся УСЛОВИЕМ ОТБОРА: ответ `aud1` открывал лист условий с
// галочками, и до настройки одного бота было четыре экрана. Теперь это тот же шаг «Что
// запускает», что у обычного бота: строки входов и «Добавить вход». Один вход «Прошёл
// бота» = ОДИН бот со своей настройкой «как прошёл»; «Получил метку» один на цепочку и
// держит несколько меток (любая из них запускает). Между входами «ИЛИ».

/** Ответ анкеты со списком входов — строка JSON `ChainEntry[]`. */
export const CHAIN_ENTRIES_FIELD = 'entries';

/** Имена полей СТРАНИЦЫ ВХОДА. Плоскими ответами они не живут (вход — элемент списка
 *  внутри одного ответа), поэтому значения даёт `fieldValues`, а правку складывает
 *  обратно `applyFields` — см. WizardStepItemList. */
export const CHAIN_ENTRY_OK_FIELD = 'entOk';
/** ПОЛЕ «БОТ» СТРАНИЦЫ ВХОДА «ПРОШЁЛ БОТА» — только у входа, чей бот удалён (П203): им
 *  человек ставит на место мёртвого бота живой, не удаляя вход. У живого входа поля нет. */
export const CHAIN_ENTRY_BOT_FIELD = 'entBot';
export const CHAIN_ENTRY_TAGS_FIELD = 'entTags';
/** Поля ЧАТ-ВХОДОВ зовутся РОВНО как у бота (К12): родной экран рисует их теми же
 *  контролами (кружки слов — по имени поля, готовая ссылка — по `linkLabel`). */
export const CHAIN_ENTRY_EXACT_FIELD = 'exactWords';
export const CHAIN_ENTRY_KEYWORD_FIELD = 'keywordWords';
export const CHAIN_ENTRY_LINK_FIELD = 'linkLabel';

/**
 * ЧАТ-ТРИГГЕР ЦЕПОЧКИ В ОБЩЕМ ВИДЕ (BotEntry) и обратно — им общий модуль
 * (`trigger-step.ts`) строит меню, строки, страницы и правку: у цепочки и у бота
 * Телеграма это ОДИН И ТОТ ЖЕ триггер (К18.1 п.9). Свои виды цепочки («Прошёл бота»,
 * «Получил метку») моделью входов бота не выражаются — им отдаётся `null`.
 */
export function chainChatOf(e: ChainEntry): BotEntry | null {
  if (e.kind === 'bot' || e.kind === 'tags') return null;
  if (e.kind === 'exact' || e.kind === 'keyword') {
    return botEntry('message', e.kind, { keywords: e.keywords });
  }
  if (e.kind === 'link') return botEntry('message', 'link', { linkLabel: e.linkLabel });
  return botEntry('message', e.kind);
}

/** Обратно: общий чат-триггер → элемент списка цепочки. */
export function chainChatBack(e: BotEntry): ChainEntry {
  if (e.kind === 'exact' || e.kind === 'keyword') return { kind: e.kind, keywords: e.keywords };
  if (e.kind === 'link') return { kind: 'link', linkLabel: e.linkLabel };
  return { kind: e.kind as 'start' | 'any' };
}

/** Разбор списка входов ДЛЯ АНКЕТЫ: мягкий. Отличие от normalizeChainEntries одно —
 *  только что добавленный «Получил метку» ещё БЕЗ меток здесь остаётся (иначе строка
 *  исчезала бы сразу после добавления); в базу такой вход не уедет — шаг заблокирован. */
function readEntries(raw: string): ChainEntry[] {
  let v: unknown;
  try {
    v = JSON.parse(raw);
  } catch {
    return [];
  }
  if (!Array.isArray(v)) return [];
  const out: ChainEntry[] = [];
  for (const x of v) {
    if (typeof x !== 'object' || x === null) continue;
    const o = x as Record<string, unknown>;
    if (o.kind === 'bot') {
      const draftId = typeof o.draftId === 'string' ? o.draftId.trim() : '';
      if (draftId === '' || out.some((e) => e.kind === 'bot' && e.draftId === draftId)) continue;
      const ok = o.ok === 'ok' || o.ok === 'fail' ? o.ok : undefined;
      out.push({ kind: 'bot', draftId, ...(ok ? { ok } : {}) });
    } else if (o.kind === 'tags') {
      if (out.some((e) => e.kind === 'tags')) continue;
      const tags = Array.isArray(o.tags)
        ? [...new Set(o.tags.filter((t): t is string => typeof t === 'string' && t.trim() !== '').map((t) => t.trim()))]
        : [];
      out.push({ kind: 'tags', tags });
    } else if ((TG_ENTRY_KINDS as readonly string[]).includes(o.kind as string)) {
      // ЧАТ-ВХОД (К10): вид один на цепочку, как у бота, — КРОМЕ «Перехода по ссылке»
      // (К18.1 п.5): ссылок сколько угодно, одинаковым не бывает их ХВОСТ. Только что
      // добавленный — ещё БЕЗ слов и без подписи: в анкете он живёт, в базу не уедет
      // (шаг заблокирован); пустой хвост дублем не считается.
      const kind = o.kind as BotEntryKind;
      if (kind !== 'link' && out.some((e) => e.kind === kind)) continue;
      if (kind === 'exact' || kind === 'keyword') {
        const keywords = Array.isArray(o.keywords)
          ? [
              ...new Set(
                o.keywords
                  .filter((w): w is string => typeof w === 'string' && w.trim() !== '')
                  .map((w) => w.trim()),
              ),
            ]
          : [];
        out.push({ kind, keywords });
      } else if (kind === 'link') {
        // ДУБЛЬ ХВОСТА ЗДЕСЬ НЕ ВЫКИДЫВАЕТСЯ (К19.1 п.6, владелец 06.09.2026): пока
        // человек набирает подпись второй ссылки, она на миг совпадает с первой — и
        // триггер исчезал из списка прямо во время набора, оставляя открытую страницу
        // пустой. Обе ссылки живут в анкете; про совпадение говорит `trigBlocked` и
        // подсказка под полем, а в базу такой список не уедет (строгий
        // `normalizeChainEntries` — без правок).
        const linkLabel = typeof o.linkLabel === 'string' ? o.linkLabel.trim().slice(0, 120) : '';
        out.push({ kind: 'link', linkLabel });
      } else {
        out.push({ kind: kind as 'start' | 'any' });
      }
    }
    if (out.length >= CHAIN_MAX_ENTRIES) break;
  }
  return out;
}

/**
 * ВХОДЫ ЦЕПОЧКИ ИЗ ОТВЕТОВ. Нет ответа `entries` — черновик СТАРЫЙ: запуск лежал
 * условиями первого письма (`aud1`), и его раскладывает адаптер (К4). Обратно пишется
 * только `entries`.
 */
export function chainEntries(answers: Record<string, unknown>): ChainEntry[] {
  const raw = ansStr(answers, CHAIN_ENTRIES_FIELD, 8000);
  if (raw !== '') return readEntries(raw);
  return chainEntriesOfAudience(chainAudience(answers, 1));
}

/** «ЗАВЕРШАТЬ ТОЛЬКО ВЫБРАННЫЕ» — id черновиков цепочек из ответа `endChainIds`
 *  (BACKLOG п.19 (3)). Ответ приходит списком (галочки) или строкой через запятую. */
export function chainEndChainIds(answers: Record<string, unknown>): string[] {
  const raw = answers.endChainIds;
  const list = Array.isArray(raw) ? raw : typeof raw === 'string' ? raw.split(',') : [];
  return [...new Set(list.map((x) => String(x).trim()).filter((x) => x !== ''))].slice(0, 50);
}

/**
 * ПЕРЕИМЕНОВАНИЕ МЕТКИ В ОТВЕТАХ АНКЕТЫ ЦЕПОЧКИ (П196.5). Анкета — источник следующей
 * версии: переписав только базу, мы получили бы старое имя обратно первым же «Сохранить».
 * Места: вход «Получил метку» (`entries`), «Кому» каждого сообщения (`cond<i>`), запуск
 * старого вида (`aud<i>`), кнопки собранного окном письма (`msg<i>`), подписи кнопок
 * письма старого вида (`msgBtns<i>`, строки «подпись|адрес» — подпись и есть метка).
 * null — метка в анкете не участвует.
 */
export function chainRenameTagInAnswers(
  answers: Record<string, unknown>,
  from: string,
  to: string,
): Record<string, unknown> | null {
  let out: Record<string, unknown> | null = null;
  const put = (key: string, value: unknown): void => {
    out = { ...(out ?? answers), [key]: value };
  };
  const entries = renameTagInEntries(chainEntries(answers), from, to);
  if (entries) put(CHAIN_ENTRIES_FIELD, JSON.stringify(entries));
  const n = chainLetterCount(answers);
  const rewriteJson = (key: string, rename: (raw: unknown) => unknown | null): void => {
    const raw = ansStr(answers, key, 200_000);
    if (raw === '') return;
    let parsed: unknown;
    try {
      parsed = JSON.parse(raw);
    } catch {
      return;
    }
    const next = rename(parsed);
    if (next) put(key, JSON.stringify(next));
  };
  for (let i = 1; i <= n; i++) {
    rewriteJson(chainField.cond(i), (raw) => renameTagInFilters(raw, from, to));
    rewriteJson(chainField.audience(i), (raw) => renameTagInFilters(raw, from, to));
    rewriteJson(chainField.message(i), (raw) => renameTagInDraft(raw, from, to));
    const legacy = ansStr(answers, chainField.buttons(i), 2000);
    if (legacy !== '') {
      const lines = legacy.split('\n');
      const next = lines.map((line) => {
        const at = line.indexOf('|');
        const text = (at === -1 ? line : line.slice(0, at)).trim();
        return text === from ? `${to}${at === -1 ? '' : line.slice(at)}` : line;
      });
      if (next.some((l, k) => l !== lines[k])) put(chainField.buttons(i), next.join('\n'));
    }
  }
  return out;
}

/** Новые ответы с этим списком входов (иммутабельно, запрет №3). */
export function chainSetEntries(
  answers: Record<string, unknown>,
  list: readonly ChainEntry[],
): Record<string, unknown> {
  return { ...answers, [CHAIN_ENTRIES_FIELD]: JSON.stringify(list) };
}

/** Сколько входов сейчас в анкете. */
export function chainEntryCount(answers: Record<string, unknown>): number {
  return chainEntries(answers).length;
}

/**
 * ДОБАВИТЬ ВХОД. `kind` — вид из меню («bot» / «tags»), `value` — выбранный бот. Второго
 * входа того же бота и второго «Получил метку» не бывает (К6): такой вызов ничего не
 * меняет, и человек просто остаётся на списке.
 */
export function chainAddEntry(
  answers: Record<string, unknown>,
  kind?: string,
  value?: string,
): Record<string, unknown> {
  const list = chainEntries(answers);
  if (list.length >= CHAIN_MAX_ENTRIES) return answers;
  if (kind === 'tags') {
    if (list.some((e) => e.kind === 'tags')) return answers;
    return chainSetEntries(answers, [...list, { kind: 'tags', tags: [] }]);
  }
  // ЧАТ-ТРИГГЕРЫ (К9 п.2): те же пять, что у бота Телеграма, и заводит их ОБЩИЙ модуль —
  // вместе с правилом «вид один раз, кроме „Перехода по ссылке“» (К18.1 п.5).
  if ((TG_ENTRY_KINDS as readonly string[]).includes(kind ?? '')) {
    const chat = trigAdd(
      'chain',
      list.map((e) => chainChatOf(e)).filter((e): e is BotEntry => e !== null),
      kind ?? '',
    );
    return chat === null ? answers : chainSetEntries(answers, [...list, chainChatBack(chat)]);
  }
  const draftId = (value ?? '').trim();
  if (kind !== 'bot' || draftId === '') return answers;
  if (list.some((e) => e.kind === 'bot' && e.draftId === draftId)) return answers;
  return chainSetEntries(answers, [...list, { kind: 'bot', draftId }]);
}

/** Убрать вход номер `i` (1…). Последний вход убирается тоже: без входов шаг просто
 *  не пускает дальше — цепочку без запуска сохранить нельзя (stepBlocked). */
export function chainRemoveEntry(
  answers: Record<string, unknown>,
  i: number,
): Record<string, unknown> {
  const list = chainEntries(answers);
  if (i < 1 || i > list.length) return answers;
  return chainSetEntries(
    answers,
    list.filter((_, idx) => idx !== i - 1),
  );
}

/** Правка СВОЕГО вида цепочки: «Как прошёл» у бота, метки у «Получил метку». */
function chainOwnApply(e: ChainEntry, patch: Record<string, unknown>): ChainEntry {
  if (e.kind === 'bot') {
    // ЗАМЕНА УДАЛЁННОГО БОТА (П203): поле «Бот» приходит только со страницы мёртвого входа.
    const botRaw = patch[CHAIN_ENTRY_BOT_FIELD];
    const draftId = typeof botRaw === 'string' && botRaw.trim() !== '' ? botRaw.trim() : e.draftId;
    if (!(CHAIN_ENTRY_OK_FIELD in patch)) return draftId === e.draftId ? e : { ...e, draftId };
    const raw = patch[CHAIN_ENTRY_OK_FIELD];
    const ok: 'ok' | 'fail' | undefined = raw === 'ok' ? 'ok' : raw === 'fail' ? 'fail' : undefined;
    return { kind: 'bot', draftId, ...(ok ? { ok } : {}) };
  }
  if (e.kind === 'tags') {
    if (!(CHAIN_ENTRY_TAGS_FIELD in patch)) return e;
    const raw = patch[CHAIN_ENTRY_TAGS_FIELD];
    const tags = Array.isArray(raw)
      ? [...new Set(raw.filter((t): t is string => typeof t === 'string' && t.trim() !== '').map((t) => t.trim()))]
      : [];
    return { kind: 'tags', tags };
  }
  return e;
}

/** Правка страницы входа: «Как прошёл» у бота, метки у «Получил метку», слова и подпись
 *  источника — у чат-входов (имена полей те же, что у бота). */
export function chainApplyEntryFields(
  answers: Record<string, unknown>,
  i: number,
  patch: Record<string, unknown>,
): Record<string, unknown> {
  const list = chainEntries(answers);
  const cur = list[i - 1];
  if (!cur) return answers;
  const next = list.map((e, idx) => {
    if (idx !== i - 1) return e;
    if (e.kind === 'bot' || e.kind === 'tags') return chainOwnApply(e, patch);
    // ЧАТ-ТРИГГЕРЫ: правку складывает ОБЩИЙ модуль (К18.1 п.9); «Команда /start» и
    // «Любое сообщение» настроек не имеют вовсе и приходят обратно как были.
    const chat = chainChatOf(e);
    return chat ? chainChatBack(trigApply('chain', chat, patch, answers)) : e;
  });
  return chainSetEntries(answers, next);
}

/** Значения полей страницы входа (их место — внутри ответа-списка, не плоско).
 *  Слова уезжают ОДНОЙ СТРОКОЙ через запятую — тот же формат, что у полей бота. */
export function chainEntryFieldValues(
  answers: Record<string, unknown>,
  i: number,
): Record<string, unknown> {
  const e = chainEntries(answers)[i - 1];
  if (!e) return {};
  if (e.kind === 'bot') return { [CHAIN_ENTRY_OK_FIELD]: e.ok ?? '', [CHAIN_ENTRY_BOT_FIELD]: e.draftId };
  if (e.kind === 'tags') return { [CHAIN_ENTRY_TAGS_FIELD]: [...e.tags] };
  const chat = chainChatOf(e);
  return chat ? trigValues('chain', chat, answers) : {};
}

/** Боты, которые уже стоят входами: второй раз их в списке выбора не показываем. */
export function chainEntryBots(answers: Record<string, unknown>): string[] {
  return chainEntries(answers)
    .filter((e): e is { kind: 'bot'; draftId: string; ok?: 'ok' | 'fail' } => e.kind === 'bot')
    .map((e) => e.draftId);
}

/**
 * ДОБАВИТЬ ПИСЬМО — новые ответы (иммутабельно, запрет №3). Ключ выдаётся СЛЕДУЮЩИМ
 * номером и больше не встретится: удалённый ключ не переиспользуется никогда, иначе
 * журнал прежнего письма молча прикрыл бы новое.
 */
export function chainAddLetter(
  answers: Record<string, unknown>,
  /** Язык анкеты: с ним новое сообщение получает НАСТОЯЩЕЕ название «Сообщение N» (П132
   *  п.7). Без него название пустое, и его показывает подстановка `chainLetterName`. */
  locale?: WizardLocale,
): Record<string, unknown> {
  const n = chainLetterCount(answers);
  if (n >= CHAIN_MAX_LETTERS) return answers;
  const next = n + 1;
  const seq = chainKeySeq(answers) + 1;
  const out: Record<string, unknown> = {
    ...answers,
    letters: next,
    letterKeys: [...chainLetterKeys(answers), `l${seq}`].join(','),
    letterSeq: seq,
  };
  // Заготовка паузы — сутки: письмо сразу за предыдущим человеку не нужно.
  for (const [f, def] of LETTER_FIELDS) out[f(next)] = def;
  // НАЗВАНИЕ — НАСТОЯЩЕЕ ЗНАЧЕНИЕ (П132 п.7, владелец 25.09.2026): «Сообщение N» с номером
  // на момент создания. После удалений соседей оно НЕ перенумеровывается — это уже
  // название, человек может его стереть или поменять.
  if (locale) out[chainField.name(next)] = chainAutoName(next, locale);
  return out;
}

/** Автоматическое название сообщения номер `n` («Сообщение 3»). */
function chainAutoName(n: number, locale: WizardLocale): string {
  return UI[locale].letterItem.replace('{n}', String(n));
}

/**
 * ОТВЕТЫ НОВОЙ ЦЕПОЧКИ СВЕРХ УМОЛЧАНИЙ АНКЕТЫ (П132 п.7): у первого сообщения название
 * «Сообщение 1» стоит значением, как у любого добавленного. Только для создания — у
 * старого черновика с пустым названием остаётся прежняя подстановка.
 */
export function chainStartAnswers(locale: WizardLocale): Record<string, unknown> {
  return { [chainField.name(1)]: chainAutoName(1, locale) };
}

/**
 * НЕТРОНУТОЕ СООБЩЕНИЕ (П132 п.3, «правило Заметок»): все ответы сообщения равны
 * заготовке `chainAddLetter` — текста, картинки, кнопок и собранного окном сообщения нет,
 * пауза заготовочная (сутки), условий нет, а название пустое или автоматическое
 * «Сообщение N». Такое сообщение, добавленное этим же открытием страницы, при уходе с
 * неё убирается молча.
 */
export function chainLetterUntouched(
  answers: Record<string, unknown>,
  i: number,
  locale: WizardLocale,
): boolean {
  for (const [f, def] of LETTER_FIELDS) {
    if (f === chainField.name) continue;
    const v = answers[f(i)];
    if (v === undefined || v === null) continue;
    if (String(v) !== String(def)) return false;
  }
  const name = answers[chainField.name(i)];
  return name === undefined || name === '' || name === chainAutoName(i, locale);
}

/**
 * ПОЧЕМУ СООБЩЕНИЕ НЕ ГОТОВО (П132 п.2): то же правило, что у «Готово» окна «Сообщение»
 * (`msgCanDone` в components/cabinet/message-rows.ts) — пусто, файл не загружен (ещё
 * грузится или сорвался), не влезает в одно сообщение. Собрано из ТЕХ ЖЕ проверок модели
 * (`isEmptyHtml`, `draftIsOneMessage`, словарь канала TELEGRAM_MESSAGE_RULES): сам
 * message-rows тянет клиентские модули, а эту анкету читает и сервер. Совпадение с
 * `msgCanDone` стережёт тест в chain.test.ts. Приколотых кнопок у сообщений цепочки нет.
 * null — сообщение готово.
 */
export type ChainLetterIssue = 'empty' | 'pending' | 'failed' | 'split';

export function chainLetterIssue(answers: Record<string, unknown>, i: number): ChainLetterIssue | null {
  const draft = chainMessage(answers, i);
  if (!draft) {
    // Сообщение старого вида (текст / картинка / кнопки отдельными ответами).
    const filled =
      ansStr(answers, chainField.text(i), 3000) !== '' || ansStr(answers, chainField.photo(i), 500) !== '';
    return filled ? null : 'empty';
  }
  if (draft.media.some((m) => m.pending === true)) return 'pending';
  if (draft.media.some((m) => m.failed === true)) return 'failed';
  if (isEmptyHtml(draft.text) && draft.media.length === 0) return 'empty';
  return draftIsOneMessage(draft, TELEGRAM_MESSAGE_RULES) ? null : 'split';
}

/** Слово причины для строки «Сообщение» и для отказа «Дальше». */
function chainIssueWord(issue: ChainLetterIssue, locale: WizardLocale): string {
  const t = UI[locale];
  if (issue === 'pending') return t.fileLoading;
  if (issue === 'failed') return t.fileFailed;
  if (issue === 'split') return t.notOneMessage;
  return t.notFilled;
}

/**
 * УДАЛИТЬ ПИСЬМО номер `i`. Поля следующих писем СДВИГАЮТСЯ на его место (номер поля —
 * это позиция), а строка ключей теряет ровно один ключ: позиции переехали, а
 * СТАБИЛЬНЫЕ id писем остались за своими письмами. Последнее письмо удалить нельзя —
 * цепочка без письма не цепочка.
 */
export function chainRemoveLetter(
  answers: Record<string, unknown>,
  i: number,
): Record<string, unknown> {
  const n = chainLetterCount(answers);
  if (n <= 1 || i < 1 || i > n) return answers;
  const keys = chainLetterKeys(answers);
  const out: Record<string, unknown> = { ...answers };
  for (let k = i; k < n; k++) {
    for (const [f, def] of LETTER_FIELDS) out[f(k)] = answers[f(k + 1)] ?? def;
  }
  for (const [f] of LETTER_FIELDS) delete out[f(n)];
  out.letters = n - 1;
  out.letterKeys = keys.filter((_, idx) => idx !== i - 1).join(',');
  out.letterSeq = chainKeySeq(answers);
  return out;
}

/**
 * ПЕРЕСТАВИТЬ ПИСЬМО с места `from` на место `to` (оба 1…N). Порядок писем и есть
 * порядок отправки, а СТАБИЛЬНЫЕ ключи едут вместе со своими письмами — поэтому журнал
 * «это человек уже получал» переживает любую перестановку (§11.8б).
 */
export function chainMoveLetter(
  answers: Record<string, unknown>,
  from: number,
  to: number,
): Record<string, unknown> {
  const n = chainLetterCount(answers);
  if (from === to || from < 1 || to < 1 || from > n || to > n) return answers;
  const keys = chainLetterKeys(answers);
  // Снимок каждого письма — по местам, а не по именам: имена и есть места.
  const snap = Array.from({ length: n }, (_, idx) =>
    Object.fromEntries(LETTER_FIELDS.map(([f, def]) => [f(1), answers[f(idx + 1)] ?? def])),
  );
  const order = keys.map((_, idx) => idx);
  const [moved] = order.splice(from - 1, 1);
  order.splice(to - 1, 0, moved!);
  const out: Record<string, unknown> = { ...answers };
  order.forEach((src, idx) => {
    for (const [f, def] of LETTER_FIELDS) out[f(idx + 1)] = snap[src]![f(1)] ?? def;
  });
  out.letterKeys = order.map((src) => keys[src]!).join(',');
  return out;
}

// ── Слова анкеты и собранной картинки схемы ───────────────────────────────────────────
//
// Язык выбирается ОДИН раз, в момент создания цепочки, и запекается в черновик
// (wizard.locale): смена языка кабинета потом тексты готовой цепочки не переписывает.

const TXT = {
  ru: {
    start: 'Начало цепочки',
    // КАРТИНКУ СХЕМЫ ЧЕЛОВЕК ТОЖЕ ЧИТАЕТ (карточка бота): слова там те же, что в анкете —
    // «сообщение», а не «письмо» (владелец 04.09.2026, И10 п.10).
    letter: 'Сообщение',
    photo: 'Картинка к сообщению',
    // Входов у цепочки семь видов (К9), и запускает её ЛЮБОЙ — сами входы дописываются
    // к этой строке словами при сборке картинки.
    noteStart: 'Цепочка запускается по любому из вариантов запуска',
    notePause: (words: string) => `Отправляется ${words} после предыдущего сообщения`,
    noteFirst: 'Отправляется сразу после входа в цепочку',
  },
  en: {
    start: 'Chain start',
    letter: 'Message',
    photo: 'Message image',
    noteStart: 'The chain starts on any of its start options',
    notePause: (words: string) => `Sent ${words} after the previous message`,
    noteFirst: 'Sent right after the user enters the chain',
  },
} as const;

/** Подписи анкеты (то, что человек читает в мастере). */
const UI = {
  ru: {
    leadTitle: 'Цепочка рассылок',
    leadText:
      'После завершения разговора с ботом пользователь получает серию сообщений по расписанию: первое сразу, следующие через заданные интервалы. Вы задаёте тексты и паузы между сообщениями, отправку выполняет бот.',
    startTitle: 'Что запускает',
    // ПОДВОДКА БЕЗ СЛОВА «ТРИГГЕР» (ТЗ USERTEST-1014 п.4, владелец 14.10.2026).
    startIntro:
      'Укажите, когда цепочка начинает отправку: например, пользователь прошёл другой ваш бот или получил метку. Сообщения получат только те, кто выполнит условие после включения цепочки. Можно добавить несколько вариантов — цепочка запустится по любому из них.',
    // ВИДЫ ВХОДА (К3): имя и описание — те же две строки, что у входов обычного бота.
    kindBot: 'Прошёл бота',
    kindBotHint: 'Пользователь завершил другой ваш бот или цепочку. У бота можно уточнить, дошёл ли он до цели.',
    kindTags: 'Получил метку',
    kindTagsHint: 'Пользователю поставили метку: на карточке, кнопкой «Поставить метку» в сообщении или шагом «Записать в карточку».',
    entryBotRow: 'Прошёл бота «{name}»',
    entryTagsRow: 'Получил метку',
    // «Как прошёл» — ТЕ ЖЕ слова, что в листе условий (второго словаря не заводим).
    okTitle: 'Результат',
    okHint:
      '«Дошёл до цели» считается так же, как конверсия на карточке бота. «Не дошёл» — в течение суток после разговора цель не достигнута.',
    okAnyFull: 'Любой результат',
    okYesFull: 'Дошёл до цели',
    okNoFull: 'За сутки до цели не дошёл',
    // ВХОД ОТ ЦЕПОЧКИ (BACKLOG п.27): исход один — отправлено последнее сообщение, вопроса «Результат» нет.
    chainDoneSub: 'После последнего сообщения цепочки',
    chainDoneNotice: 'Цепочка запустится, когда «{name}» отправит человеку своё последнее сообщение.',
    entryTagsTitle: 'Метки',
    entryTagsHint: 'Цепочка запустится от любой из отмеченных.',
    tagsEmpty: 'Меток пока нет.',
    botGone: 'Бот удалён',
    botGoneSub: 'Бот удалён — выберите другой',
    botGoneOpt: '{name} — удалён',
    needEntryAlive: 'Бот {names} удалён. Выберите другой бот или добавьте другой вариант запуска',
    needEntryAliveMany: 'Боты {names} удалены. Выберите другой бот или добавьте другой вариант запуска',
    entryBotField: 'Бот',
    entryBotFieldHint: 'Этот бот удалён. Выберите другой — его прохождение запустит цепочку.',
    tagGone: 'метка удалена',
    botUnfit: 'Этот бот не подходит: цепочки работают только с ботами в Telegram.',
    pickBotTitle: 'Прошёл бота',
    notChosen: 'Не выбрано',
    needEntry: 'Добавьте хотя бы один вариант запуска',
    needEntryTags: 'Выберите хотя бы одну метку',
    // ЧАТ-ВХОДЫ (К12): имена и описания видов берутся у бота (tgEntryKindParams) — здесь
    // только слова блокировки, которых у бота нет. Страницы «Настроек у этого входа нет»
    // больше не бывает (К16.1 П5): вход без настроек добавляется сразу, строкой.
    needEntryWords: 'Впишите хотя бы одно кодовое слово',
    needEntryLink: 'Укажите название источника ссылки',
    bot: 'Бот',
    botHint: 'Бот, от имени которого отправляются сообщения цепочки.',
    startEmpty: 'Не заполнено',
    letterTitle: (i: number) => `Сообщение ${i}`,
    letterIntro: 'Напишите текст сообщения. Можно добавить изображение и кнопки.',
    letterIntroNext: 'Это сообщение отправляется через заданный интервал после предыдущего. Интервал задаётся ниже.',
    text: 'Текст',
    textPh: 'Напишите сообщение',
    photo: 'Картинка',
    photoHint: 'Отправляется вместе с сообщением. Необязательно.',
    buttons: 'Кнопки',
    buttonsHint: 'Одна кнопка на строку. Чтобы добавить ссылку, укажите её после знака «|».',
    waitTitle: 'Пауза перед сообщением',
    waitAmount: 'Интервал',
    waitUnit: 'Единица времени',
    unitMinutes: 'минутах',
    unitHours: 'часах',
    unitDays: 'днях',
    // У первого сообщения пауза считается ОТ ЗАПУСКА, у остальных — от предыдущего.
    waitFirstNow: 'Сразу после запуска',
    waitAfterStart: 'Интервал после запуска',
    doneTitle: 'Готово',
    // ДВА ТЕКСТА ШАГА «Готово» (владелец 16.09.2026, М1.2): в создании — про то, что
    // будет после кнопки, в правке — про то, что сохраняется. Прежний текст про
    // «закончит выбранного бота» врал: вход у цепочки бывает семи видов.
    doneIntro: 'Проверьте настройки и нажмите «Создать». Цепочка начнёт работать сразу после создания.',
    doneIntroEdit: 'Проверьте изменения и нажмите «Сохранить».',
    apply: 'Кого касается правка',
    applyHint: 'Некоторые пользователи уже получают сообщения этой цепочки. Выберите, применять ли изменения к ним.',
    applyNew: 'Только новых',
    applyNewHint:
      'Изменения не затронут пользователей, которые начали получать сообщения до правки: они получат цепочку в прежнем виде.',
    applyAll: 'Всех',
    applyAllHint:
      'Изменения затронут и тех, кто уже получает сообщения. Полученные сообщения повторно не отправляются, следующие приходят в новом виде.',
    // ПЕРЕХОДЫ МЕЖДУ ЦЕПОЧКАМИ — СВОЙ ШАГ СРАЗУ ПОСЛЕ «Что запускает» (владелец
    // 16.09.2026, М1.1: «ты выбираешь на шаге 2, что запускает. Дальше логично спросить,
    // завершать ли другие цепочки при запуске этой»). Слово везде — «остановить».
    othersTitle: 'Другие цепочки',
    othersIntro:
      'Пользователь может состоять в нескольких цепочках одновременно. При запуске этой цепочки можно остановить его другие цепочки, чтобы ограничить количество сообщений.',
    endOthers: 'При запуске этой цепочки',
    endOthersHint: '',
    endNone: 'Не останавливать другие',
    endNoneHint: 'Другие цепочки продолжат отправлять свои сообщения',
    endAll: 'Остановить все другие',
    endAllHint:
      'Все другие цепочки этого пользователя будут остановлены. Неотправленные сообщения не будут доставлены.',
    endSome: 'Остановить выбранные',
    endSomeHint: 'Будут остановлены только отмеченные ниже цепочки.',
    endChains: 'Какие цепочки остановить',
    endChainsHint: 'Отметьте цепочки, которые будут остановлены при запуске этой.',
    endNoChains: 'Других цепочек пока нет. Когда они появятся, их можно будет отметить здесь.',
    // НАЗВАНИЕ ЦЕПОЧКИ (владелец 16.09.2026, М1.3: «я не понимаю, почему там текст
    // первого сообщения»). Пусто — заголовок по-прежнему считается по первому сообщению.
    chainTitle: 'Название',
    chainTitlePh: 'Например: Прогрев после вебинара',
    chainTitleHint: 'Отображается только вам: в списке ботов и в карточке пользователя.',
    needBot: 'Выберите бота, который будет отправлять сообщения',
    needText: 'Напишите текст сообщения или приложите картинку',
    lettersTitle: 'Сообщения',
    lettersIntro: 'Каждое сообщение — отдельный шаг: время отправки, условия и текст.',
    letterAdd: 'Добавить сообщение',
    letterEmpty: 'Добавьте хотя бы одно сообщение',
    // Красная кнопка — ОДНИМ словом (К18.1 п.8): и у сообщений, и у триггеров.
    letterRemove: 'Удалить',
    letterItem: 'Сообщение {n}',
    rowName: 'Название',
    nameHint: 'Для себя: видите только вы, клиенту не уходит',
    rowWait: 'Когда',
    rowWho: 'Условия',
    rowMsg: 'Сообщение',
    waitNow: 'сразу',
    waitFirst: 'Сразу после запуска',
    inheritFrom: 'Из сообщения {n}',
    inheritGot: 'Получали сообщение {n}',
    whoFootFirst:
      'В цепочку попадут пользователи, которые выполнят условия после её включения. Тем, кто выполнил их ранее, сообщения не отправляются.',
    whoTitle: 'Кто получит',
    whoHint: 'Условия проверяются в момент отправки. Если они не выполнены, пользователь исключается из цепочки.',
    whoAll: 'Без условий',
    whoPassedFact: 'проходил бота',
    whoHas: 'есть метка',
    whoLacks: 'нет метки',
    whoReceived: 'получал сообщение',
    okYes: 'дошёл до цели',
    okNo: 'за сутки до цели не дошёл',
    okAny: 'любой результат',
    whoChosen: 'выбрано вручную: {n}',
    whoExcept: 'кроме выбранных: {n}',
    notFilled: 'Не заполнено',
    // ВИД МЕДИА В СТРОКЕ СООБЩЕНИЯ БЕЗ ТЕКСТА (П194). Слова — те же, что у окна
    // «Сообщение» (messages: cabinet.message.kind_*, viewVoice, sumAlbum, sumMany); словарь
    // окна сюда не тянем (600 КБ в клиентский пакет), совпадение держит тест chain.test.ts.
    mediaPhoto: 'Фото',
    mediaVideo: 'Видео',
    mediaAnimation: 'Гифка',
    mediaVoice: 'Голосовое сообщение',
    mediaRound: 'Кружок',
    mediaAudio: 'Аудио',
    mediaDocument: 'Файл',
    mediaAlbum: 'Альбом ({n})',
    mediaMany: '{kind} ({n})',
    tagBotPass: 'прошёл бота «{name}»',
    tagBotGoal: 'дошёл до цели в «{name}»',
    tagBotPassShort: 'прошёл бота',
    tagBotGoalShort: 'дошёл до цели',
    whoNotPassed: 'не проходил бота',
    techTag: 'служебная отметка',
    needLetter: 'Напишите сообщение',
    // ПРИЧИНЫ «СООБЩЕНИЕ НЕ ГОТОВО» (П132 п.2): значение строки «Сообщение» серым.
    fileLoading: 'Файл ещё загружается',
    fileFailed: 'Файл не загружен',
    notOneMessage: 'Не влезет в одно сообщение',
    // ВОПРОС ПЕРЕД «УДАЛИТЬ» НА СТРАНИЦЕ СООБЩЕНИЯ (П132 п.5).
    removeAskTitle: 'Удалить сообщение?',
    removeAskText: 'Текст и вложения этого сообщения будут удалены.',
  },
  en: {
    leadTitle: 'Message chain',
    leadText:
      'After a conversation with your bot ends, the user receives a series of scheduled messages: the first one right away, the next ones after the intervals you set. You write the texts and set the pauses; the bot handles the sending.',
    startTitle: 'What starts it',
    startIntro:
      'Specify when the chain starts sending: for example, the user completed another of your bots or got a tag. Only those who meet the condition after the chain is turned on will receive the messages. You can add several options — the chain starts on any of them.',
    kindBot: 'Finished the bot',
    kindBotHint: 'The user completed another bot or chain of yours. For a bot you can specify whether they reached the goal.',
    kindTags: 'Got the label',
    kindTagsHint: 'The user got a label: on the card, from an “Add a label” button in a message, or from the “Save to the card” step.',
    entryBotRow: 'Finished the “{name}” bot',
    entryTagsRow: 'Got the label',
    okTitle: 'Result',
    okHint:
      '“Reached the goal” is counted the same way as the conversion on the bot card. “Did not reach” — the goal was not reached within a day after the conversation.',
    okAnyFull: 'Any result',
    okYesFull: 'Reached the goal',
    okNoFull: 'Did not reach the goal within a day',
    chainDoneSub: 'After the chain’s last message',
    chainDoneNotice: 'The chain will start when “{name}” sends the person its last message.',
    entryTagsTitle: 'Labels',
    entryTagsHint: 'Any of the selected labels starts the chain.',
    tagsEmpty: 'No labels yet.',
    botGone: 'Bot deleted',
    botGoneSub: 'Bot deleted — choose another',
    botGoneOpt: '{name} — deleted',
    needEntryAlive: 'Bot {names} is deleted. Choose another bot or add another start option',
    needEntryAliveMany: 'Bots {names} are deleted. Choose another bot or add another start option',
    entryBotField: 'Bot',
    entryBotFieldHint: 'This bot is deleted. Choose another — finishing it will start the chain.',
    tagGone: 'label deleted',
    botUnfit: 'This bot cannot be used: chains work only with Telegram bots.',
    pickBotTitle: 'Finished the bot',
    notChosen: 'Not selected',
    needEntry: 'Add at least one start option',
    needEntryTags: 'Select at least one label',
    needEntryWords: 'Add at least one code word',
    needEntryLink: 'Name the link source',
    bot: 'Bot',
    botHint: 'The bot that sends the chain messages.',
    startEmpty: 'Not filled in',
    letterTitle: (i: number) => `Message ${i}`,
    letterIntro: 'Write the message text. You can add an image and buttons.',
    letterIntroNext: 'This message is sent after a set interval following the previous one. The interval is set below.',
    text: 'Text',
    textPh: 'Write the message',
    photo: 'Image',
    photoHint: 'Sent together with the message. Optional.',
    buttons: 'Buttons',
    buttonsHint: 'One button per line. To add a link, put it after the “|” sign.',
    waitTitle: 'Pause before this message',
    waitAmount: 'Interval',
    waitUnit: 'Time unit',
    unitMinutes: 'minutes',
    unitHours: 'hours',
    unitDays: 'days',
    waitFirstNow: 'Right after the start',
    waitAfterStart: 'Interval after the start',
    doneTitle: 'Done',
    doneIntro: 'Check the settings and press “Create”. The chain starts working right after it is created.',
    doneIntroEdit: 'Check the changes and press “Save”.',
    apply: 'Who the edit applies to',
    applyHint: 'Some users are already receiving messages from this chain. Choose whether the changes apply to them.',
    applyNew: 'New users only',
    applyNewHint:
      'The changes do not affect users who started receiving messages before the edit: they receive the chain as it was.',
    applyAll: 'All users',
    applyAllHint:
      'The changes also apply to users who are already receiving messages. Messages they have received are not sent again; the next ones arrive in the new form.',
    othersTitle: 'Other chains',
    othersIntro:
      'A user can be in several chains at the same time. When this chain starts, you can stop the user’s other chains to limit the number of messages.',
    endOthers: 'When this chain starts',
    endOthersHint: '',
    endNone: 'Do not stop the others',
    endNoneHint: 'The other chains keep sending their messages',
    endAll: 'Stop all the others',
    endAllHint: 'All other chains of this user will be stopped. Unsent messages will not be delivered.',
    endSome: 'Stop the selected ones',
    endSomeHint: 'Only the chains selected below will be stopped.',
    endChains: 'Which chains to stop',
    endChainsHint: 'Select the chains that will be stopped when this one starts.',
    endNoChains: 'There are no other chains yet. Once they appear, you can select them here.',
    chainTitle: 'Name',
    chainTitlePh: 'For example: Warm-up after the webinar',
    chainTitleHint: 'Visible only to you: in the bot list and on the user card.',
    needBot: 'Select the bot that will send the messages',
    needText: 'Write the message text or attach an image',
    lettersTitle: 'Messages',
    lettersIntro: 'Each message is a separate step: send time, conditions and the text itself.',
    letterAdd: 'Add a message',
    letterEmpty: 'Add at least one message',
    letterRemove: 'Delete',
    letterItem: 'Message {n}',
    rowName: 'Name',
    nameHint: 'For you only: the client never sees it',
    rowWait: 'When',
    rowWho: 'Conditions',
    rowMsg: 'Message',
    waitNow: 'right away',
    waitFirst: 'Right after the start',
    inheritFrom: 'From message {n}',
    inheritGot: 'Got message {n}',
    whoFootFirst:
      'The chain includes users who meet the conditions after it is turned on. Users who met them earlier do not receive messages.',
    whoTitle: 'Who receives it',
    whoHint: 'Conditions are checked at send time. If they are not met, the user is removed from the chain.',
    whoAll: 'No conditions',
    whoPassedFact: 'completed the bot',
    whoHas: 'has the label',
    whoLacks: 'no label',
    whoReceived: 'got the message',
    okYes: 'reached the goal',
    okNo: 'did not reach the goal within a day',
    okAny: 'any result',
    whoChosen: 'selected manually: {n}',
    whoExcept: 'except selected: {n}',
    notFilled: 'Not filled in',
    mediaPhoto: 'Photo',
    mediaVideo: 'Video',
    mediaAnimation: 'GIF',
    mediaVoice: 'Voice message',
    mediaRound: 'Video circle',
    mediaAudio: 'Audio',
    mediaDocument: 'File',
    mediaAlbum: 'Album ({n})',
    mediaMany: '{kind} ({n})',
    tagBotPass: 'finished the “{name}” bot',
    tagBotGoal: 'reached the goal in “{name}”',
    tagBotPassShort: 'finished the bot',
    tagBotGoalShort: 'reached the goal',
    whoNotPassed: 'did not complete the bot',
    techTag: 'service mark',
    needLetter: 'Write the message',
    fileLoading: 'The file is still uploading',
    fileFailed: 'The file was not uploaded',
    notOneMessage: 'Does not fit into one message',
    removeAskTitle: 'Delete the message?',
    removeAskText: 'The text and attachments of this message will be deleted.',
  },
} as const;

/** Ключ страницы «Что запускает» — БОТ-ОТПРАВИТЕЛЬ И ШАГ-СПИСОК ВХОДОВ (К15 П4). */
export const CHAIN_START_STEP = 'start';
/** Шаг «Бот» цепочки (19.09.2026). */
export const CHAIN_ACCOUNT_STEP = 'account';
/** Ключ страницы «Письма» (список шагов цепочки). */
export const CHAIN_LETTERS_STEP = 'letters';
/** Ключ шага «Другие цепочки» — стоит СРАЗУ ЗА «Что запускает» (владелец 16.09.2026). */
export const CHAIN_OTHERS_STEP = 'others';

/** Слова заметки «других цепочек пока нет» — их ставит панель мастера на место мёртвого
 *  набора галочек (М1.1). Словарь у шага один: второго текста для того же места нет. */
export function chainNoOthersWords(locale: WizardLocale): string {
  return UI[locale].endNoChains;
}
export const CHAIN_DONE_STEP = 'done';

/** СКРЫТЫЙ ОТВЕТ «В ЦЕПОЧКЕ ЕСТЬ ЛЮДИ» (MSG-VERIFY-FIX В, 25.10.2026): ставит панель правки по числу идущих и ждущих;
 *  на сервер не едет — снимается вместе с `wizEditing`. По нему виден переключатель «Кого касается правка». */
export const WIZ_CHAIN_RUNNING = 'wizChainRunning';

/**
 * БОТ ВХОДА УДАЛЁН (П203, владелец 29.09.2026: бот удалился тихо, а цепочка осталась
 * «включена»). Живые списки приехали, и бота нет ни среди годных, ни среди негодных —
 * значит его удалили: такой вход сработать не может никогда (отметку «прошёл бота» ставит
 * только живой бот). Без списков (сервер) — не судим: пустой список не доказательство.
 */
export function chainBotGone(draftId: string, live?: WizardItemLive): boolean {
  if (!live) return false;
  if ((live.botsGone ?? []).some((b) => b.value === draftId)) return true;
  return !live.bots.some((b) => b.value === draftId) && !(live.botsUnfit ?? []).some((b) => b.value === draftId);
}

/** Имена удалённых ботов входов — в кавычках, через запятую (для замка «Дальше»). */
function chainGoneNames(list: readonly ChainEntry[], locale: WizardLocale, live?: WizardItemLive): string[] {
  return list
    .filter((e): e is { kind: 'bot'; draftId: string; ok?: 'ok' | 'fail' } => e.kind === 'bot')
    .map((e) => `«${botLabel(e.draftId, locale, live)}»`);
}

/** Имя бота из живого списка кабинета (живые, негодные, удалённые — имя есть у всех, П203);
 *  нет нигде — «Бот удалён» (К3). */
function botLabel(id: string, locale: WizardLocale, live?: WizardItemLive): string {
  return (
    live?.bots.find((b) => b.value === id)?.label ||
    live?.botsUnfit?.find((b) => b.value === id)?.label ||
    live?.botsGone?.find((b) => b.value === id)?.label ||
    UI[locale].botGone
  );
}

/** «Как прошёл» словами — те же три строки, что в листе условий. */
function okWords(ok: 'ok' | 'fail' | undefined, locale: WizardLocale): string {
  const t = UI[locale];
  return ok === 'ok' ? t.okYesFull : ok === 'fail' ? t.okNoFull : t.okAnyFull;
}

/** Строка СВОЕГО вида цепочки: «Прошёл бота «X»» / «Получил метку». */
function chainOwnLine(
  e: ChainEntry,
  locale: WizardLocale,
  live?: WizardItemLive,
): { title: string; sub: string; chevron?: boolean; invalid?: boolean } {
  const t = UI[locale];
  if (e.kind === 'bot') {
    // БОТ НЕ ТЕЛЕГРАМА (BACKLOG п.20): вход сохранён раньше, а сработать не может — словами.
    const unfit = live?.botsUnfit?.find((b) => b.value === e.draftId);
    if (unfit && !live?.bots.some((b) => b.value === e.draftId)) {
      return { title: t.entryBotRow.replace('{name}', unfit.label), sub: t.botUnfit };
    }
    // БОТ УДАЛЁН (П203): строка отбита сигнально — подстрока словами, `invalid` красит её
    // (веб — роль красного, натив — `.systemRed`); страница входа даёт выбрать другой бот.
    if (chainBotGone(e.draftId, live)) {
      return { title: t.entryBotRow.replace('{name}', botLabel(e.draftId, locale, live)), sub: t.botGoneSub, invalid: true };
    }
    return {
      title: t.entryBotRow.replace('{name}', botLabel(e.draftId, locale, live)),
      // ИСТОЧНИК — ЦЕПОЧКА (BACKLOG п.27): исход один, «как прошёл» не спрашивается.
      sub: live?.botsChains?.includes(e.draftId) ? t.chainDoneSub : okWords(e.ok, locale),
    };
  }
  // МЕТКА, КОТОРОЙ В КАБИНЕТЕ БОЛЬШЕ НЕТ (П196.5): вход остаётся (условие хранит имя),
  // но человеку это сказано словами — как «Бот удалён» у входа «Прошёл бота». Живого списка
  // нет (сервер) или он пуст (ещё не приехал) — подписи не ставим: пустой список не
  // доказательство удаления.
  const known = live?.tags ?? [];
  const tags = (e.kind === 'tags' ? e.tags : []).map((tag) =>
    known.length > 0 && !known.some((o) => o.value === tag) ? `${tag} (${t.tagGone})` : tag,
  );
  return { title: t.entryTagsRow, sub: tags.length > 0 ? tags.join(', ') : t.notChosen };
}

/** Строка списка входов: заголовок и подстрока словами (К3). */
export function chainEntryLine(
  answers: Record<string, unknown>,
  i: number,
  locale: WizardLocale,
  live?: WizardItemLive,
): { title: string; sub: string; chevron?: boolean; invalid?: boolean } {
  const e = chainEntries(answers)[i - 1];
  if (!e) return { title: '', sub: '' };
  if (e.kind === 'bot' || e.kind === 'tags') return chainOwnLine(e, locale, live);
  // ЧАТ-ТРИГГЕР: строку строит ОБЩИЙ модуль — та же, что у бота Телеграма (К18.1 п.9).
  // Живые списки нужны и ему: подстрока «Перехода по ссылке» — сама ссылка целиком
  // (К19.1 п.5), а логин бота знает только страница мастера.
  return trigLine('chain', locale, chainChatOf(e) ?? botEntry('message', 'start'), live);
}

/** Заголовок страницы входа: имя бота либо «Получил метку». */
export function chainEntryTitle(
  answers: Record<string, unknown>,
  i: number,
  locale: WizardLocale,
  live?: WizardItemLive,
): string {
  const e = chainEntries(answers)[i - 1];
  if (!e) return UI[locale].entryTagsRow;
  if (e.kind === 'bot') return botLabel(e.draftId, locale, live);
  if (e.kind === 'tags') return UI[locale].entryTagsRow;
  return trigTitle('chain', locale, chainChatOf(e) ?? botEntry('message', 'start'));
}

/** Поля страницы СВОЕГО вида: «Как прошёл» из трёх (radio) либо метки галочками (К3, К5). */
function chainOwnFields(
  e: ChainEntry,
  locale: WizardLocale,
  live?: WizardItemLive,
): CatalogParam[] {
  const t = UI[locale];
  if (e.kind === 'bot') {
    // ИСТОЧНИК — ЦЕПОЧКА (BACKLOG п.27, владелец 23.10.2026): у цепочки исход ОДИН — «отправлено последнее
    // сообщение», поэтому вместо вопроса «Результат» страница говорит словами, когда сработает вход.
    if (live?.botsChains?.includes(e.draftId) && !chainBotGone(e.draftId, live)) {
      return [
        {
          name: 'entChainDone',
          type: 'notice',
          default: '',
          displayName: t.chainDoneNotice.replace('{name}', botLabel(e.draftId, locale, live)),
        },
      ];
    }
    return [
      // БОТ УДАЛЁН (П203) — первым полем выбор ЖИВОГО бота на его место: строка списка
      // ведёт сюда, и человек чинит вход, не удаляя его. У живого входа этого поля нет.
      ...(chainBotGone(e.draftId, live)
        ? [
            {
              name: CHAIN_ENTRY_BOT_FIELD,
              type: 'options',
              default: e.draftId,
              displayName: t.entryBotField,
              description: t.entryBotFieldHint,
              options: [
                { name: t.botGoneOpt.replace('{name}', botLabel(e.draftId, locale, live)), value: e.draftId },
                ...(live?.bots ?? []).map((b) => ({ name: b.label, value: b.value })),
              ],
            } satisfies CatalogParam,
          ]
        : []),
      {
        name: CHAIN_ENTRY_OK_FIELD,
        type: 'options',
        default: '',
        displayName: t.okTitle,
        description: t.okHint,
        options: [
          { name: t.okAnyFull, value: '' },
          { name: t.okYesFull, value: 'ok' },
          { name: t.okNoFull, value: 'fail' },
        ],
      },
    ];
  }
  // СЛУЖЕБНЫХ ОТМЕТОК В ВЫБОРЕ НЕТ (К6 п.4; владелец 13.09: они не метки): «прошёл
  // бота» — это отдельный вид входа, а сырой ключ `bot:<id>:passed` человеку ничего не
  // говорит. Свод и так их не отдаёт, фильтр здесь — замок.
  const options = (live?.tags ?? [])
    .filter((o) => !isServiceMark(o.value))
    .map((o) => ({ name: o.label, value: o.value }));
  // Метка, которой в своде больше нет (людей с ней удалили), из выбора не пропадает:
  // молча выкидывать чужой выбор нельзя.
  for (const tag of e.kind === 'tags' ? e.tags : []) {
    if (!options.some((o) => o.value === tag)) options.unshift({ name: tag, value: tag });
  }
  if (options.length === 0) {
    return [{ name: 'entTagsNone', type: 'notice', default: '', displayName: t.tagsEmpty }];
  }
  return [
    {
      name: CHAIN_ENTRY_TAGS_FIELD,
      type: 'multiOptions',
      default: [],
      displayName: t.entryTagsTitle,
      description: t.entryTagsHint,
      options,
    },
  ];
}

/** Поля страницы входа: свои виды цепочки — здесь, чат-триггеры — общим модулем. */
export function chainEntryFields(
  answers: Record<string, unknown>,
  i: number,
  locale: WizardLocale,
  live?: WizardItemLive,
): CatalogParam[] {
  const e = chainEntries(answers)[i - 1];
  if (!e) return [];
  if (e.kind === 'bot' || e.kind === 'tags') return chainOwnFields(e, locale, live);
  // ЧАТ-ТРИГГЕРЫ: поля РОВНО те же, что у бота (`exactWords`, `keywordWords`,
  // `linkLabel`), и строит их ОБЩИЙ модуль — своего набора полей у цепочки нет
  // (К18.1 п.9). «Команда /start» и «Любое сообщение» страницы не имеют вовсе.
  return trigFields('chain', locale, chainChatOf(e) ?? botEntry('message', 'start'));
}

/**
 * ВИДЫ ДЛЯ МЕНЮ «ДОБАВИТЬ ТРИГГЕР». Уже добавленный вид ИЗ МЕНЮ НЕ УХОДИТ (К18.1 п.3,
 * владелец 06.09.2026: «либо не показывается, либо неактивная, как правильно по канонам
 * iPhone»): он остаётся на своём месте серым, с причиной «Уже добавлено». Порядок видов
 * прежний (К9 п.2).
 *
 * «Прошёл бота» анкета погасить не может — свободных ботов она не знает: их считает
 * страница мастера, она же ставит виду `disabled` и причину (К18.3 этап 1 п.1).
 * «Переход по ссылке» не гасится НИКОГДА: ссылок бывает сколько угодно (К18.1 п.5).
 */
function chainOwnKinds(list: readonly ChainEntry[], locale: WizardLocale): WizardItemAddKind[] {
  const t = UI[locale];
  const w = wizEntryWords(locale);
  // «Получил метку» ОДИН на цепочку: у метки настроек нет, метки живут в одном входе.
  const tagsTaken = list.some((e) => e.kind === 'tags');
  return [
    { key: 'bot', title: t.kindBot, description: t.kindBotHint, pick: 'bots' },
    {
      key: 'tags',
      title: t.kindTags,
      description: t.kindTagsHint,
      ...(tagsTaken ? { disabled: true, disabledHint: w.already } : {}),
    },
  ];
}

export function chainEntryKinds(
  answers: Record<string, unknown>,
  locale: WizardLocale,
): WizardItemAddKind[] {
  const list = chainEntries(answers);
  const out: WizardItemAddKind[] = [...chainOwnKinds(list, locale)];
  // ПЯТЬ ТРИГГЕРОВ БОТА ТЕЛЕГРАМА — ПОД ДВУМЯ НОВЫМИ (заказ владельца 05.09.2026, К9 п.2:
  // «использовать все триггеры, что используются для другого шаблона Telegram, их 5
  // штук, и ещё добавить сверху два новых»). Строит их ОБЩИЙ модуль — тот же, что у
  // ботов: имена, описания, серые пункты и порядок одинаковые (К18.1 п.9).
  out.push(
    ...trigAddKinds(
      'chain',
      locale,
      list.map((e) => chainChatOf(e)).filter((e): e is BotEntry => e !== null),
    ),
  );
  return out;
}

/**
 * ШАГ «ЧТО ЗАПУСКАЕТ» — ТОТ ЖЕ ШАГ-СПИСОК, ЧТО У ОБЫЧНОГО БОТА (К2 п.1). Строки входов
 * плюс пунктирная «Добавить вход»; тап по строке открывает страницу входа, внизу неё —
 * «Удалить вход». Галочек нет вовсе: строка списка И ЕСТЬ состояние (К2 п.3).
 *
 * Перестановки НЕТ: порядок входов ничего не значит — сработал любой (К3).
 *
 * ОТДЕЛЬНОГО ШАГА «АККАУНТ» НЕТ (владелец 06.09.2026, К15 П4 — вариант Б: «Пустая
 * страница, да? Как-то мало тут всего»): строка «Бот» стоит СВЕРХУ этого шага, над
 * списком входов, и шагов у цепочки три. Прежнее правило родного мастера «у шага-списка
 * полей на экране нет» снято аддитивно (К17.2 п.1): поля шага уезжают нативу ПЕРЕД
 * полем-списком, натив рисует их в одном стеке в порядке прихода.
 */
/**
 * СВОИ ВИДЫ ЦЕПОЧКИ СВЕРХУ ОБЩЕГО МЕНЮ (К9 п.2): «Прошёл бота» и «Получил метку».
 * Общий строитель шага про них ничего не знает — он спрашивает эти руки.
 */
function chainOwn(locale: WizardLocale): TriggerOwn<ChainEntry> {
  const isOwn = (e: ChainEntry): boolean => e.kind === 'bot' || e.kind === 'tags';
  return {
    keys: ['bot', 'tags'],
    is: isOwn,
    kinds: (list) => chainOwnKinds(list, locale),
    add: (list, kind, value) => {
      if (kind === 'tags') {
        return list.some((e) => e.kind === 'tags') ? null : { kind: 'tags', tags: [] };
      }
      const draftId = (value ?? '').trim();
      if (kind !== 'bot' || draftId === '') return null;
      return list.some((e) => e.kind === 'bot' && e.draftId === draftId)
        ? null
        : { kind: 'bot', draftId };
    },
    line: (e, live) => chainOwnLine(e, locale, live),
    title: (e, live) => (e.kind === 'bot' ? botLabel(e.draftId, locale, live) : UI[locale].entryTagsRow),
    fields: (e, live) => chainOwnFields(e, locale, live),
    values: (e) =>
      e.kind === 'bot'
        ? { [CHAIN_ENTRY_OK_FIELD]: e.ok ?? '', [CHAIN_ENTRY_BOT_FIELD]: e.draftId }
        : { [CHAIN_ENTRY_TAGS_FIELD]: [...(e.kind === 'tags' ? e.tags : [])] },
    apply: (e, patch) => chainOwnApply(e, patch),
    pickTaken: (list) =>
      list.filter((e): e is { kind: 'bot'; draftId: string; ok?: 'ok' | 'fail' } => e.kind === 'bot').map((e) => e.draftId),
  };
}

/** Предзаполнение «О боте» у цепочки: имя — «Цепочка рассылок», описание — текст знакомства. */
function chainAbout(locale: WizardLocale): { title: string } {
  return { title: UI[locale].leadTitle };
}

/** Шаг «Бот»: бот-отправитель — отдельным шагом перед «Что запускает», как аккаунт у
 *  остальных анкет (владелец 19.09.2026). */
function accountStep(locale: WizardLocale): WizardStep {
  const t = UI[locale];
  return {
    key: CHAIN_ACCOUNT_STEP,
    title: t.bot,
    intro: t.botHint,
    params: [
      {
        ...wizAccountParam('telegram', locale),
        displayName: t.bot,
        description: t.botHint,
        typeOptions: { ownLabel: true },
      },
    ],
  };
}

function startStep(locale: WizardLocale): WizardStep {
  const t = UI[locale];
  return triggerStep<ChainEntry>(locale, {
    kinds: 'chain',
    key: CHAIN_START_STEP,
    title: t.startTitle,
    intro: t.startIntro,
    max: CHAIN_MAX_ENTRIES,
    // Бот-отправитель — ОДНО поле шага, над списком (К15 П4). Поля самих триггеров живут
    // на СВОЕЙ странице, на шаге их нет.
    // Название и бот-отправитель ушли на свои шаги («О боте», «Бот» — владелец
    // 19.09.2026: «у цепочки шаг 2 разделить: сначала аккаунт, как у остальных, потом
    // что запускает»). Тут — только входы.
    params: [],
    store: { read: chainEntries, write: chainSetEntries },
    chat: { of: chainChatOf, make: chainChatBack },
    own: chainOwn(locale),
  });
}

// ── Шаг «Письма» — СПИСОК ШАГОВ (ТЗ В2, В4) ───────────────────────────────────────────
//
// Страниц-повторов у цепочки больше нет: письма стоят СПИСКОМ, а одно письмо
// настраивается своей страницей из трёх строк — «Сколько ждать», «Кому слать»,
// «Сообщение». Ответы при этом прежние, плоские (номер письма в имени поля), поэтому
// старая цепочка открывается без потерь.

/**
 * Поля страницы «Когда». У ПЕРВОГО СООБЩЕНИЯ ОНИ ТЕПЕРЬ ЕСТЬ ТОЖЕ (владелец 04.09.2026,
 * И10 п.10): «Сразу после запуска» — это 0, а можно поставить и паузу «через N часов или
 * дней после запуска». Поля те же (`waitAmount1`/`waitUnit1`), просто раньше первое
 * письмо принудительно обнулялось и строка была неизменяемой.
 */
function waitFields(i: number, locale: WizardLocale): CatalogParam[] {
  const t = UI[locale];
  return [
    {
      name: chainField.waitAmount(i),
      type: 'number',
      // У первого сообщения умолчание — НОЛЬ («сразу после запуска»), у остальных сутки.
      default: i === 1 ? 0 : 1,
      displayName: i === 1 ? t.waitAfterStart : t.waitAmount,
      typeOptions: { minValue: 0, maxValue: 365 },
    },
    {
      name: chainField.waitUnit(i),
      type: 'options',
      default: 'days',
      displayName: t.waitUnit,
      options: [
        // МИНУТЫ — ПЕРВОЙ СТРОКОЙ (владелец 21.09.2026): короткая пауза после запуска
        // нужна чаще всего, а порядок единиц идёт от мелкой к крупной.
        { name: t.unitMinutes, value: 'minutes' },
        { name: t.unitHours, value: 'hours' },
        { name: t.unitDays, value: 'days' },
      ],
    },
  ];
}

/**
 * Пауза словами: «сразу», «2 дня», «6 часов». У ПЕРВОГО сообщения нулевая пауза читается
 * «Сразу после запуска» (владелец 04.09.2026, И10 п.10): у него отсчёт идёт от входа в
 * цепочку, а не от предыдущего сообщения, — и человек должен видеть, от чего именно.
 */
export function chainWaitWords(answers: Record<string, unknown>, i: number, locale: WizardLocale): string {
  const t = UI[locale];
  const letter = chainLetters(answers)[i - 1];
  if (!letter || letter.delayMin === 0) return i === 1 ? t.waitFirst : t.waitNow;
  return pauseWords(letter, locale);
}

/** МЕТКА СЛОВАМИ. Служебную отметку («прошёл бота»), если она попалась в старом условии,
 *  человек читает словами — сырой ключ ему не показываем (Е7). */
export function chainTagWords(tag: string, locale: WizardLocale): string {
  const sys = parseBotMark(tag);
  if (!sys) return tag;
  const t = UI[locale];
  return sys.kind === 'goal' ? t.tagBotGoalShort : t.tagBotPassShort;
}

/**
 * ЗАПУСК СЛОВАМИ — СТРОКАМИ СПИСКА ВХОДОВ (К4, последний пункт). Прежняя сводка условий
 * (`chainWhoWords` над `aud1`, `audWords` с временем «событие») отменена вместе с самим
 * `aud1`: второго набора слов про запуск в продукте нет — и строка списка, и подпись на
 * картинке схемы, и «Кто получит» первого сообщения читают ЭТУ функцию.
 *
 * Имена ботов приходят живым списком кабинета; без него бот читается «Бот удалён» —
 * заглушек вида «прошёл бота» без имени тут не бывает.
 */
export function chainEntriesWords(
  answers: Record<string, unknown>,
  locale: WizardLocale,
  live?: WizardItemLive,
): string {
  const list = chainEntries(answers);
  if (list.length === 0) return UI[locale].startEmpty;
  return list
    .map((_, idx) => {
      const line = chainEntryLine(answers, idx + 1, locale, live);
      return line.sub === '' ? line.title : `${line.title} · ${line.sub}`;
    })
    .join(' · ');
}

/**
 * «КОМУ» СООБЩЕНИЯ СЛОВАМИ (владелец 04.09.2026, И10 п.10) — своя строка условий, а не
 * запуск. Пусто — «Все, кто дошёл до этого сообщения»: наследование условий никуда не
 * делось, просто СВОИХ условий у сообщения нет.
 */
export function chainCondWords(answers: Record<string, unknown>, i: number, locale: WizardLocale): string {
  // УСЛОВИЯ — ФАКТЫ (И12): «проходил бота», «есть метка».
  return audWords(chainConditions(answers, i), locale, UI[locale].whoAll);
}

/**
 * УСЛОВИЯ СООБЩЕНИЯ СЛОВАМИ (чисто). Времени «событие» здесь больше нет: события живут
 * входами и читаются chainEntriesWords (К4) — эта функция говорит только про ФАКТЫ
 * («проходил бота», «есть метка»), как и положено условию «Кому» (И12 п.2).
 */
function audWords(aud: AudienceFilters | null, locale: WizardLocale, empty: string): string {
  const t = UI[locale];
  if (!aud) return empty;
  const passedWord = t.whoPassedFact;
  const hasWord = t.whoHas;
  // ИМЁН БОТОВ И ПИСЕМ ЗДЕСЬ НЕ БЫВАЕТ: функция чистая, живых списков кабинета не знает,
  // а язык у неё запечён в черновик. Полная сводка с именами — на экране «Кто получит»
  // (он же зовёт эту самую audienceWords, только со своим словарём имён).
  const dict: AudienceWordsDict = {
    passed: (names) => (names === '' ? passedWord : `${passedWord}, ${names}`),
    // Имён ботов здесь не бывает (функция чистая) — остаётся одно слово условия.
    notPassed: () => t.whoNotPassed,
    okYes: t.okYes,
    okNo: t.okNo,
    okAny: t.okAny,
    // ИМЁН БОТОВ ЗДЕСЬ НЕ БЫВАЕТ — остаются только настройки: «прошёл бота, дошёл до
    // цели». Промежуток тоже не называется: функция чистая, календаря локали не знает.
    bot: (_name, tail) => tail,
    range: () => '',
    // Служебная отметка (старое условие) читается словами: сырой ключ ничего не говорит.
    tagName: (tag) => (isServiceMark(tag) ? t.techTag : tag),
    received: () => t.whoReceived,
    has: (names) => `${hasWord}: ${names}`,
    lacks: (names) => `${t.whoLacks}: ${names}`,
    chosen: (n) => t.whoChosen.replace('{n}', String(n)),
    except: (n) => t.whoExcept.replace('{n}', String(n)),
    botName: (id) => id,
    letterName: () => '',
  };
  return audienceWords(aud, dict) || empty;
}

/** Предел названия письма для себя (П129.10): строка списка и заголовок карточки. */
export const CHAIN_LETTER_NAME_MAX = 40;

/** Название письма для себя; пусто — «Сообщение N» (П129.10). */
export function chainLetterName(answers: Record<string, unknown>, i: number, locale: WizardLocale): string {
  const own = ansStr(answers, chainField.name(i), CHAIN_LETTER_NAME_MAX);
  return own !== '' ? own : UI[locale].letterItem.replace('{n}', String(i));
}

/** Предел третьей части строки письма (первая строка текста или вид медиа). */
const CHAIN_PREVIEW_MAX = 60;

/** Имя файла без расширения: «Montserrat-Light.ttf» → «Montserrat-Light» (П194). */
function fileStem(name: string): string {
  const clean = name.trim();
  const dot = clean.lastIndexOf('.');
  return dot > 0 ? clean.slice(0, dot) : clean;
}

/**
 * ЧТО В ПИСЬМЕ БЕЗ ТЕКСТА — СЛОВОМ ВИДА (ТЗ П194, владелец 28.09.2026: у кружка, голосового,
 * фото без подписи строка списка молчала). «Кружок», «Голосовое сообщение», «Фото»,
 * «Альбом (3)»; трек — «Аудио · <название>», файл — «Файл · <имя без расширения>», когда
 * имя известно; несколько треков/файлов — «Аудио (N)» / «Файл (N)». Медиа нет — пусто.
 */
export function chainMediaWords(draft: TgDraft, locale: WizardLocale): string {
  const t = UI[locale];
  const first = draft.media[0];
  if (!first) return '';
  const n = draft.media.length;
  const shape = draftShape(draft);
  if (shape === 'album') return t.mediaAlbum.replace('{n}', String(n));
  if (shape === 'audioAlbum') return t.mediaMany.replace('{kind}', t.mediaAudio).replace('{n}', String(n));
  if (shape === 'documentAlbum') return t.mediaMany.replace('{kind}', t.mediaDocument).replace('{n}', String(n));
  if (first.kind === 'audio') {
    const title = (first.title ?? '').trim() || fileStem(first.name ?? '');
    return title !== '' ? `${t.mediaAudio} · ${title}` : t.mediaAudio;
  }
  if (first.kind === 'document') {
    const stem = fileStem(first.name ?? '');
    return stem !== '' ? `${t.mediaDocument} · ${stem}` : t.mediaDocument;
  }
  const word: Record<Exclude<typeof first.kind, 'audio' | 'document'>, string> = {
    photo: t.mediaPhoto,
    video: t.mediaVideo,
    animation: t.mediaAnimation,
    voice: t.mediaVoice,
    round: t.mediaRound,
  };
  return word[first.kind];
}

/**
 * Первая строка письма — по ней человек узнаёт его в списке. Текста нет — вид медиа
 * (П194, `chainMediaWords`); нет ничего — пусто (строка «Сообщение» скажет «Не заполнено»).
 */
export function chainLetterPreview(answers: Record<string, unknown>, i: number, locale: WizardLocale): string {
  const draft = chainMessage(answers, i);
  const text = draft ? htmlToPlain(draft.text) : ansStr(answers, chainField.text(i), 3000);
  const line = (text.split('\n').find((x) => x.trim()) ?? '').trim();
  if (line !== '') return line.slice(0, CHAIN_PREVIEW_MAX);
  if (draft && draft.media.length > 0) return chainMediaWords(draft, locale).slice(0, CHAIN_PREVIEW_MAX);
  // Старое письмо (до окна «Сообщение»): картинка отдельным полем — это фото.
  return !draft && ansStr(answers, chainField.photo(i), 500) !== '' ? UI[locale].mediaPhoto : '';
}

/** Шаг «Письма»: список писем, страница письма — три строки. */
function lettersStep(locale: WizardLocale): WizardStep {
  const t = UI[locale];
  return {
    key: CHAIN_LETTERS_STEP,
    title: t.lettersTitle,
    intro: t.lettersIntro,
    // Поля письма живут на СВОЕЙ странице (rows), на шаге их нет: шаг — это список.
    params: [],
    itemList: {
      max: CHAIN_MAX_LETTERS,
      itemAdd: t.letterAdd,
      itemEmpty: t.letterEmpty,
      itemRemove: t.letterRemove,
      // ЗАГОЛОВОК — НАЗВАНИЕ ДЛЯ СЕБЯ (П129.10), пусто — «Сообщение N».
      itemTitle: (answers, i) => chainLetterName(answers, i, locale),
      // «КОГДА» — БАРАБАНАМИ (П130): число и единица свайпом, клавиатуры нет.
      pageWheel: (_answers, _i, row) => row === 'wait',
      count: chainLetterCount,
      // СТРОКА СПИСКА — ОДНА строка значения с многоточием (владелец 04.09.2026, И10 п.2
      // и п.10): «через 2 дня · нет метки «купил» · Как успехи?…». Событие запуска в неё
      // не попадает: оно живёт на шаге «С чего начинается», а не у сообщения.
      line: (answers, i) => ({
        title: chainLetterName(answers, i, locale),
        sub: [chainWaitWords(answers, i, locale), chainCondWords(answers, i, locale), chainLetterPreview(answers, i, locale)]
          .filter((x) => x !== '')
          .join(' · '),
      }),
      rows: (answers, i) => [
        {
          // НАЗВАНИЕ ДЛЯ СЕБЯ — ПЕРВЫМ РЯДОМ (П129.10, владелец 24.09.2026: «может, я хочу
          // написать для себя, чтобы в списке было не „Сообщение 2“»): до 40 знаков,
          // клиенту не уходит; пусто — «Сообщение N».
          key: 'name',
          title: t.rowName,
          value: chainLetterName(answers, i, locale),
          kind: 'fields',
          fields: [
            {
              name: chainField.name(i),
              type: 'string',
              default: '',
              displayName: t.rowName,
              placeholder: t.letterItem.replace('{n}', String(i)),
              description: t.nameHint,
              typeOptions: { maxLength: CHAIN_LETTER_NAME_MAX },
            },
          ],
        },
        {
          key: 'wait',
          // «КОГДА» ЕСТЬ У КАЖДОГО СООБЩЕНИЯ, ВКЛЮЧАЯ ПЕРВОЕ (владелец 04.09.2026,
          // И10 п.10): у первого пауза считается ОТ ЗАПУСКА, у остальных — от
          // предыдущего. Прежняя неизменяемая строка первого письма отменена.
          title: t.rowWait,
          value: chainWaitWords(answers, i, locale),
          kind: 'fields',
          fields: waitFields(i, locale),
        },
        {
          key: 'who',
          title: t.rowWho,
          value: chainCondWords(answers, i, locale),
          // ЛИСТ УСЛОВИЙ БЕЗ СПИСКА ЛЮДЕЙ (владелец 04.09.2026, И10 п.10): «кому»
          // проверяется В МОМЕНТ ОТПРАВКИ, и показывать сейчас, кто подходит, — врать.
          // Экран живых людей остаётся у РАЗОВОЙ рассылки: там отправка здесь и сейчас.
          kind: 'conditions',
          // УСЛОВИЯ СООБЩЕНИЯ — ТОЛЬКО МЕТКИ (владелец 05.09.2026): «есть метка» / «нет
          // метки», проверка в момент отправки. Это не «кому» рассылки: человек сюда
          // попал по запуску, отбирать заново некого.
          only: 'conditions',
          param: chainField.cond(i),
          setKey: chainLetterKeys(answers)[i - 1] ?? `l${i}`,
          empty: t.whoAll,
        },
        (() => {
          // ГОДНОСТЬ СООБЩЕНИЯ — НА ЭТОЙ СТРОКЕ (П132 п.2, решение В5): галочки у окна
          // «Сообщение» больше нет, и узнать «не готово» человеку негде, кроме этой строки.
          // Значение серое, как обычное: первая фраза текста или слово причины. «Готово»
          // страницы по признаку `invalid` не выпускает сообщение и подсвечивает строку.
          const issue = chainLetterIssue(answers, i);
          return {
            key: 'msg',
            title: t.rowMsg,
            value: issue ? chainIssueWord(issue, locale) : chainLetterPreview(answers, i, locale) || t.notFilled,
            kind: 'message' as const,
            param: chainField.message(i),
            ...(issue ? { invalid: true } : {}),
          };
        })(),
      ],
      add: (answers) => chainAddLetter(answers, locale),
      remove: chainRemoveLetter,
      move: chainMoveLetter,
      untouched: (answers, i) => chainLetterUntouched(answers, i, locale),
      removeAsk: () => ({ title: t.removeAskTitle, text: t.removeAskText, ok: t.letterRemove }),
    },
  };
}

/**
 * Шаг «ДРУГИЕ ЦЕПОЧКИ» — сразу после «Что запускает» (владелец 16.09.2026, М1.1: «ты
 * выбираешь на шаге 2, что запускает. Дальше логично спросить, завершать ли другие
 * цепочки при запуске этой. Именно в этом месте, на следующем шаге»). Раньше оба поля
 * стояли на «Готово», в самом конце.
 *
 * Список цепочек кабинета подставляет панель мастера (typeOptions.liveChains) — анкета
 * чистая и кабинета не видит; в приложении поле едет обычным набором галочек
 * (multiselect), Swift не менялся.
 *
 * ВАРИАНТ «Остановить выбранные» ВИДЕН ВСЕГДА (владелец 16.09.2026: «где вариант
 * выбрать, какие завершать? Его нет»): прежний фильтр панели, прятавший его при пустом
 * списке цепочек, отменён. Других цепочек нет — вместо пустого набора галочек стоит
 * строка-заметка (её подставляет та же панель).
 */
function othersStep(locale: WizardLocale): WizardStep {
  const t = UI[locale];
  return {
    key: CHAIN_OTHERS_STEP,
    title: t.othersTitle,
    intro: t.othersIntro,
    params: [
      {
        name: 'endOthers',
        type: 'options',
        required: true,
        default: 'none',
        displayName: t.endOthers,
        options: [
          { name: t.endNone, value: 'none', description: t.endNoneHint },
          { name: t.endAll, value: 'all', description: t.endAllHint },
          { name: t.endSome, value: 'some', description: t.endSomeHint },
        ],
      },
      // НЕ обязательное (М1.1): «Остановить выбранные» без единой галочки — законный
      // ответ (движку останавливать нечего), и «Дальше» он запирать не должен.
      {
        name: 'endChainIds',
        type: 'multiOptions',
        default: [],
        displayName: t.endChains,
        description: t.endChainsHint,
        options: [],
        typeOptions: { liveChains: true },
        displayOptions: { show: { endOthers: ['some'] } },
      },
    ],
  };
}

/**
 * Страница «Готово». На ней же живёт ПЕРЕКЛЮЧАТЕЛЬ ПРАВКИ — и только в правке: поле
 * показывается по ответу `wizEditing`, который панель ставит сама, когда анкету открыли
 * у существующей цепочки. При создании применять правку не к кому.
 *
 * ТЕКСТ ШАГА РАЗНЫЙ (М1.2): в создании — что будет после «Создать», в правке — что
 * сохраняется. Настройка «Другие цепочки» отсюда УШЛА на свой шаг (М1.1).
 */
function doneStep(locale: WizardLocale, editing = false): WizardStep {
  const t = UI[locale];
  return {
    key: CHAIN_DONE_STEP,
    title: t.doneTitle,
    intro: editing ? t.doneIntroEdit : t.doneIntro,
    params: [
      {
        name: 'applyTo',
        type: 'options',
        required: true,
        default: 'new',
        displayName: t.apply,
        description: t.applyHint,
        // Переключатель нужен, только когда есть кого переключать (MSG-VERIFY-FIX В): в пустой цепочке шаг «Готово»
        // остаётся одним текстом, а правка применяется как «Только новых».
        displayOptions: { show: { wizEditing: [true], [WIZ_CHAIN_RUNNING]: [true] } },
        options: [
          { name: t.applyNew, value: 'new', description: t.applyNewHint },
          { name: t.applyAll, value: 'all', description: t.applyAllHint },
        ],
      },
    ],
  };
}

// ── Картинка схемы ────────────────────────────────────────────────────────────────────

/**
 * ТЕКСТ ПИСЬМА ДЛЯ КАРТИНКИ СХЕМЫ. Письмо нового вида собрано окном «Сообщение» и
 * лежит моделью — на картинке показываем его текст без разметки; старое письмо отдаёт
 * свой текст как есть.
 */
function letterWords(letter: ChainLetter): string {
  const draft = letter.message ? toTgDraft(letter.message) : null;
  return draft ? htmlToPlain(draft.text) : letter.text;
}

/** Кнопки письма для картинки схемы (у нового письма они внутри модели). */
function letterButtons(letter: ChainLetter): ChainButton[] {
  const draft = letter.message ? toTgDraft(letter.message) : null;
  if (!draft) return letter.buttons;
  return draft.buttons
    .flat()
    .map((b) => ({ text: b.text, url: b.kind === 'url' ? b.url : '' }))
    .slice(0, 8);
}

/** Шаг «Письмо» Телеграма с кнопками (форма — как у анкет-сестёр). */
function messageNode(id: string, name: string, letter: ChainLetter, startName: string, text: string): WorkflowNode {
  const rows = letterButtons(letter).map((b, i) => ({
    row: {
      buttons: [
        {
          text: b.text,
          additionalFields: b.url ? { url: b.url } : { callback_data: `chain-${id}-${i}` },
        },
      ],
    },
  }));
  return {
    id,
    name,
    type: 'n8n-nodes-base.telegram',
    typeVersion: 1.2,
    position: [0, 0],
    parameters: {
      resource: 'message',
      operation: 'sendMessage',
      chatId: chatStartJson(startName, 'message.chat.id'),
      text,
      ...(rows.length > 0 ? { replyMarkup: 'inlineKeyboard', inlineKeyboard: { rows } } : {}),
    },
  };
}

/** Пауза словами для подписи шага («2 дня», «6 часов», «5 мин.»). */
function pauseWords(letter: ChainLetter, locale: WizardLocale): string {
  // ПАУЗА КОРОЧЕ ЧАСА ЧИТАЕТСЯ МИНУТАМИ (владелец 21.09.2026): округление до часов
  // показывало «0 ч.» у минутной паузы.
  if (letter.delayMin < 60) {
    const m = letter.delayMin;
    return locale === 'en' ? `${m} minute${m === 1 ? '' : 's'}` : `${m} мин.`;
  }
  const hours = Math.round(letter.delayMin / 60);
  if (hours % 24 === 0 && hours >= 24) {
    const d = hours / 24;
    return locale === 'en' ? `${d} day${d === 1 ? '' : 's'}` : `${d} дн.`;
  }
  return locale === 'en' ? `${hours} hour${hours === 1 ? '' : 's'}` : `${hours} ч.`;
}

export const chainWizard: TemplateWizardDef = {
  id: CHAIN_WIZARD_ID,
  // Шаблона нет: цепочка рождается целиком из ответов (см. standalone в core.ts).
  templateSource: '',
  standalone: true,
  accountChannel: 'telegram',

  // Сцена первого экрана — «Цепочка рассылок» (s5): человек прошёл воронку, а дальше
  // получает письма по дням. Ту же сцену он видел в мини-туре до регистрации.
  demoScene: 's5',

  lead(locale) {
    const t = UI[locale];
    return { title: t.leadTitle, text: t.leadText };
  },

  // ФРАЗЫ ЭТОГО БОТА В ТЕЛЕФОНЕ ПЕРВОГО ЭКРАНА (О5.8, владелец 03.09.2026: настроенный
  // бот в превью показывает СВОИ данные, все четыре превью). Сцена «Цепочка рассылок»
  // (s5) — четыре письма по дням; ставим реальные письма человека по порядку в те же
  // места (msg1..msg4). Задал меньше четырёх — лишние места остаются текстом сцены;
  // задал больше — в сцене четыре окна, показываем первые. Паузы (дни) сцены не трогаем.
  sceneTexts(answers) {
    const letters = chainLetters(answers);
    const out: SceneTexts = {};
    const slots = ['msg1', 'msg2', 'msg3', 'msg4'] as const;
    letters.slice(0, slots.length).forEach((letter, i) => {
      // Письмо, собранное окном «Сообщение», отдаёт свой текст без разметки: телефон
      // первого экрана обязан показывать то же, что придёт человеку.
      const text = letterWords(letter).trim();
      if (text) out[slots[i]!] = text;
    });
    return out;
  },

  // Честный статичный набор: по нему считаются стартовые ответы и его читают тесты.
  // Живые страницы (по странице на письмо) даёт dynamicSteps.
  // СТРАНИЦ-ПОВТОРОВ БОЛЬШЕ НЕТ (ТЗ 03.09.2026 В2): письма стоят СПИСКОМ на одном шаге,
  // а письмо настраивается своей страницей. Число шагов анкеты от ответов не зависит —
  // счётчик «Шаг N из M» больше не пляшет от количества писем.
  // ЧЕТЫРЕ ШАГА (М1.1, владелец 16.09.2026): «Что запускает» (название, бот-отправитель
  // и список входов) → «Другие цепочки» → «Сообщения» → «Готово». Отдельного шага
  // «Аккаунт» больше нет — на нём стояла одна строка.
  steps(locale) {
    return [aboutStep(locale, chainAbout(locale)), accountStep(locale), startStep(locale), othersStep(locale), lettersStep(locale), doneStep(locale)];
  },

  // ЖИВЫЕ СТРАНИЦЫ. Отличаются от честного статичного набора ОДНИМ: текстом шага
  // «Готово» (в правке он про сохранение, а не про включение — М1.2). Число шагов от
  // ответов не зависит, счётчик «Шаг N из M» не пляшет.
  dynamicSteps(answers, locale) {
    return [
      aboutStep(locale, chainAbout(locale)),
      accountStep(locale),
      startStep(locale),
      othersStep(locale),
      lettersStep(locale),
      doneStep(locale, answers.wizEditing === true),
    ];
  },

  stepBlocked(answers, stepKey, locale, live) {
    const t = UI[locale];
    if (stepKey === CHAIN_ACCOUNT_STEP) {
      return ansStr(answers, 'accountId', 100) === '' ? t.needBot : null;
    }
    if (stepKey === CHAIN_START_STEP) {
      // БОТ-ОТПРАВИТЕЛЬ — ПЕРВОЕ, чего не хватает: он стоит верхней строкой того же шага.
      // БЕЗ ВХОДА цепочку начинать не с чего (К3): «Дальше» не пускает, пока список пуст
      // или у входа «Получил метку» не выбрано ни одной метки.
      const list = chainEntries(answers);
      if (list.length === 0) return t.needEntry;
      if (list.some((e) => e.kind === 'tags' && e.tags.length === 0)) return t.needEntryTags;
      // ВСЕ ВХОДЫ — УДАЛЁННЫЕ БОТЫ (П203): цепочке не с чего запуститься, «Дальше» заперт,
      // пока человек не поставит живой бот или другой триггер. Один живой вход — путь открыт.
      if (list.every((e) => e.kind === 'bot' && chainBotGone(e.draftId, live?.items))) {
        const names = chainGoneNames(list, locale, live?.items);
        return (names.length > 1 ? t.needEntryAliveMany : t.needEntryAlive).replace('{names}', names.join(', '));
      }
      // ЧАТ-ТРИГГЕР БЕЗ НАСТРОЙКИ (К12) — половинчатый: в базу он не уедет, и цепочка
      // молча не запускалась бы. Слова блокировки — ОБЩИЕ с ботами (К18.1 п.9).
      return trigBlocked(
        'chain',
        locale,
        list.map((e) => chainChatOf(e)).filter((e): e is BotEntry => e !== null),
        answers,
      );
    }
    if (stepKey === CHAIN_LETTERS_STEP) {
      // Пустое письмо цепочку ломает молча: человек получит пустоту. Говорим об этом
      // словами и не пускаем дальше (тот же приём, что у входов).
      // Правило годности — ТО ЖЕ, что у строки «Сообщение» и у окна (П132 п.2): пустое,
      // с незагруженным файлом или не влезающее в одно сообщение дальше не пускаем.
      const n = chainLetterCount(answers);
      for (let i = 1; i <= n; i++) {
        const issue = chainLetterIssue(answers, i);
        if (!issue) continue;
        const why = issue === 'empty' ? t.needLetter : chainIssueWord(issue, locale);
        return `${t.letterItem.replace('{n}', String(i))} · ${why}`;
      }
      return null;
    }
    return null;
  },

  /**
   * ВХОДЫ ЧЕРНОВИКА (К12) — ТОЛЬКО ЧАТ-ВИДЫ. Из них синк маршрутов делает строки
   * bot_dispatch, по ним же карточка бота рисует чипы «что запускает». Входы «Прошёл
   * бота» и «Получил метку» сюда не идут: моделью входов бота (BotEntry) они не
   * выражаются — их запуск живёт в `entries` версии цепочки и проверяется базой.
   */
  triggerMeta(answers) {
    return { entries: chainChatEntries(chainEntries(answers)) };
  },

  /**
   * КАРТИНКА ЦЕПОЧКИ. Канон приходит пустым (standalone) — берём из него только
   * служебные поля и кладём цепочку: «Начало цепочки» → письма подряд.
   *
   * INVARIANT(scriptera): узлов ожидания (Wait) здесь НЕ БЫВАЕТ. Паузы между письмами
   * держит наша база (bot_chains + chainPlan), и написаны они подписями шагов, а не
   * спящим прогоном. Причина: спящий прогон делает переключатель «применить к тем, кто
   * уже внутри» физически невозможным (разбор владельца 31.08.2026).
   * Канон: docs/HOW-IT-WORKS.md §11.8б.
   */
  buildWorkflow(template, answers, locale) {
    const T = TXT[locale];
    const letters = chainLetters(answers);
    const nodes: WorkflowNode[] = [];
    const connections: WorkflowJson['connections'] = {};
    const taken = new Set<string>();

    const watch = chainWatch(chainEntries(answers));
    const startName = wizNodeTitle(T.start, taken, T.start);
    nodes.push({
      id: 'chain-start',
      name: startName,
      type: CHAIN_START_TYPE,
      typeVersion: 1,
      position: [0, 0],
      parameters: {
        // ЧЕГО ЖДЁТ ЦЕПОЧКА — её СПИСОК ВХОДОВ (К4). На картинке видно то же самое, что
        // проверяет движок: он читает те же входы тем же chainWatch.
        sources: watch.bots.join(','),
        entryTags: watch.tags.join(','),
        // ВСЕ входы словами — поле информационное (картинка не исполняется): по нему
        // человек читает запуск прямо на схеме, включая чат-входы (К12).
        entries: chainEntriesWords(answers, locale),
      },
      // Подпись узла — тоже входы словами, а не одна заготовка «закончил бота»: у
      // цепочки семь видов входа, и любой из них её запускает.
      notes: `${T.noteStart}: ${chainEntriesWords(answers, locale)}`,
    });

    const link = (from: string, to: string): void => {
      connections[from] = {
        main: [[{ node: to, type: 'main', index: 0 }]],
      } as (typeof connections)[string];
    };

    let prev = startName;
    let x = 260;
    letters.forEach((letter, idx) => {
      if (letter.photo) {
        const photoName = wizNodeTitle(`${T.photo} ${idx + 1}`, taken, T.photo);
        nodes.push({
          ...wizAttachNode({
            id: `chain-photo-${letter.key}`,
            name: photoName,
            channel: 'telegram',
            kind: 'image',
            url: letter.photo,
            recipient: chatStartJson(startName, 'message.chat.id'),
          }),
          position: [x, 0],
        });
        link(prev, photoName);
        prev = photoName;
        x += 260;
      }
      const words = letterWords(letter);
      const msgName = wizNodeTitle(words, taken, `${T.letter} ${idx + 1}`);
      // Условие шага человек читает прямо на картинке: «кому слать» — часть шага, а не
      // спрятанная настройка. Запуска в подписи сообщения НЕТ (К4): он живёт входами и
      // написан у шага «Начало цепочки».
      const who = chainCondWords(answers, idx + 1, locale);
      const note = idx === 0 ? T.noteFirst : T.notePause(pauseWords(letter, locale));
      nodes.push({
        ...messageNode(`chain-msg-${letter.key}`, msgName, letter, startName, words),
        position: [x, 0],
        notes: letter.conditions ? `${note}. ${who}` : note,
      });
      link(prev, msgName);
      prev = msgName;
      x += 260;
    });

    // ИМЯ ЦЕПОЧКИ — ОТВЕТ «Название» (владелец 16.09.2026, М1.3). Пусто — как раньше, по
    // первому сообщению, но ОДНОЙ строкой и не длиннее 60 знаков: прежний заголовок
    // тащил в список ботов весь текст письма с переносами.
    // Название — с шага «О боте» (19.09.2026); `chainTitle` — прежнее поле старых анкет.
    const asked = ansStr(answers, 'botTitle', 120) || ansStr(answers, 'chainTitle', 120);
    const firstLine = (letters[0] ? letterWords(letters[0]) : '')
      .split('\n')
      .find((x) => x.trim() !== '');
    const name =
      asked !== ''
        ? asked
        : wizNodeTitle((firstLine ?? '').trim().slice(0, 60), new Set(), UI[locale].leadTitle);
    return { ...template, name, nodes, connections };
  },
};
